import MuleBadge from '../../components/MuleBadge';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { MarkerClusterer, SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import styles from './LiveMap.module.css';
import {
  TruckIcon,
  MapPinIcon,
  ClockIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  LockOpenIcon
} from '@heroicons/react/24/outline';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import { usePermissions } from '../../contexts/PermissionContext';
import GridLabel from '../../components/GridLabel';
import Notification, { NotificationContainer } from '../../components/Notification';
import TypeAhead, { TypeAheadOption } from '../../components/TypeAhead';
import { VehicleInfoDrawer } from './LiveMap/components/VehicleInfoDrawer';
import type { DriverWithMoves } from '../../components/driver-status-monitor';
import { ProDetailsView } from '../../components/ProDetailsView';
import api from '../../services/api';
import unitsService from '../../services/units.service';
import ordersService from '../../services/orders.service';
import { useMsal } from '@azure/msal-react';
import { celebrateAchievement } from '../../utils/confetti';
import userSettingsService from '../../services/userSettings.service';
import { getGoogleMapsLoaderOptions } from '../../config/googleMaps';
import { config } from '../../config/config';
import { getRegionCoordinates } from './LiveMap/config';
import { buildRegionOptions, matchesRegion, matchesDriverType, type DriverType } from './LiveMap/filters';
import { escapeHtml } from '../../utils/sanitize';
import { Tooltip } from '../../components/ui/Tooltip';
import io from 'socket.io-client';
import { msalInstance, apiTokenRequest } from '../../services/authConfig';
import PermissionsContainer from '../../components/PermissionsContainer';
import { PermissionAwareInput } from '../../contexts/PermissionAccessContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';

// Vehicle location response from new API endpoint
interface VehicleLocationResponse {
  vehicleId: string;
  vehicleName: string;
  vin: string;
  latitude: number;
  longitude: number;
  speed: number;
  heading?: number;
  // Dispatch information from cache_vehicle_location
  driverName?: string;
  unitNumber?: string;
  dispatcher?: string;
  dispatcherDisplayName?: string;
  region?: string;
  proNumber?: string;
  originName?: string;
  originCoordinates?: string;
  originArrivalDateTime?: string;
  originDepartDateTime?: string;
  destinationName?: string;
  destinationCoordinates?: string;
  arrivalDateTime?: string;
  departDateTime?: string;
  dispatchCompleted?: boolean;
  remainingDispatchesToday?: number;
  isBareChassis?: boolean;
  v1Status?: string;
  driverStatus?: string;
  // Endorsement data from cache_driver_qualification
  isDaycab?: boolean;
  hasHazmat?: boolean;
  hasTank?: boolean;
  has93k?: boolean;
  isLightTruck?: boolean;
  isMule?: boolean;
  // LOCAL / OTR driver preference from cache_driver_qualification
  preference?: string;
  // Equipment type data from dispatch
  size?: string;
  equipType?: string;
  chassisType?: string;
}

// Driver dispatch response from latest-dispatches endpoint
interface DriverDispatchResponse {
  unitNumber: string;
  status: string;
  driverName: string;
  truckVin: string;
  samsaraVehicleId: string | null;
  dispatch: {
    proNumber: string;
    dispatchStarted: string | null;
    origin: {
      name: string;
      coordinates: string;
      arrivalDateTime: string | null;
      departDateTime: string | null;
    };
    destination: {
      name: string;
      coordinates: string;
      arrivalDateTime: string | null;
      departDateTime: string | null;
    };
    legDrop: string;
    nextDispatchId: number | null;
    isCompleted: boolean;
  } | null;
}

// Vehicle marker type for map display
interface VehicleMarker {
  id: string;
  uniqueKey: string;
  name: string;
  latitude: number;
  longitude: number;
  speedMilesPerHour: number;
  headingDegrees?: number;
  vin: string;
  dispatchInfo: {
    driverName: string | null;
    unitNumber: string | null;
    dispatcher: string | null;
    dispatcherDisplayName?: string | null;
    region?: string | null;
    proNumber: string | null;
    originName: string | null;
    originCoordinates: string | null;
    originArrivalDateTime?: string | null;
    originDepartDateTime?: string | null;
    destinationName: string | null;
    destinationCoordinates: string | null;
    arrivalDateTime?: string | null;
    departDateTime?: string | null;
    isCompleted: boolean;
    remainingDispatchesToday?: number;
    isBareChassis?: boolean;
    v1Status?: string | null;
    driverStatus?: string | null;
    // Endorsement data
    isDaycab?: boolean;
    hasHazmat?: boolean;
    hasTank?: boolean;
    has93k?: boolean;
    isLightTruck?: boolean;
    isMule?: boolean;
    // LOCAL / OTR driver preference
    preference?: string | null;
    // Equipment type data
    size?: string;
    equipType?: string;
    chassisType?: string;
  } | null;
}

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '100%'
};

// Default center (Houston, TX)
const defaultCenter = {
  lat: 29.7604,
  lng: -95.3698
};

// Map options - uses mapId for AdvancedMarkerElement support
// Note: When using mapId, styles must be configured in Google Cloud Console
// To enable dark mode, configure the dark mode styling for this mapId in Cloud Console:
// https://console.cloud.google.com/google/maps-apis/studio/maps
const getMapOptions = (isDarkMode: boolean, mapType: 'roadmap' | 'hybrid' = 'roadmap'): google.maps.MapOptions => ({
  mapId: '3cf64d0fcebdaf11f3625d6e', // CleanMap style - POIs disabled with light/dark modes
  mapTypeId: mapType, // 'roadmap' or 'hybrid' (satellite with labels)
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: true,
  clickableIcons: false, // Disable clicking on POI icons
  colorScheme: isDarkMode ? 'DARK' : 'LIGHT', // Control which map style to use from Cloud Console
});

// Truck type for different cargo configurations
type TruckType = 'TruckWithContainer' | 'TruckWithChassis' | 'TruckEmpty' | 'Mule';

// Custom truck icon generator with rotation support
// Returns an HTMLElement for use with AdvancedMarkerElement
const createVehicleIcon = (
  speed: number,
  heading?: number,
  hasDispatch?: boolean,
  isCompleted?: boolean,
  remainingDispatches?: number,
  hasOriginArrivalTime?: boolean,
  truckType: TruckType = 'TruckWithContainer',
  sizeMultiplier: number = 1,
  unitNumber?: string,
  opacity: number = 1,
  isOOS: boolean = false,
  isNotWorking: boolean = false,
  darkMode: boolean = false
): HTMLElement => {
  // Outline for the icon: soft (semi-transparent) white in dark mode for gentle
  // separation against dark tiles; solid black in light mode.
  const outlineHex = darkMode ? '#FFFFFF' : '#000000';
  const outlineAlpha = darkMode ? opacity * 0.55 : opacity;
  // Determine color based on dispatch status:
  // - Selected (#000000 light mode / #FFFFFF dark mode): Selected truck (overrides all other colors)
  // - Red (#EF4444): Out Of Service (overrides all other colors except selected)
  // - Yellow (#FFFAA0): Not Working (driver marked as not working for today)
  // - Blue (#3B82F6): In Progress (has dispatch, not completed)
  // - Orange (#F59E0B): Not Started (has remaining dispatches but no origin arrival time)
  // - Grey (#6B7280): Dispatched (completed with remaining > 0 and has origin arrival time)
  // - Green (#10B981): Available (no dispatch OR completed with 0 remaining)

  // Helper function to convert hex to rgba with opacity
  const hexToRgba = (hex: string, alpha: number): string => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  let baseColor: string;
  let strokeColor: string;

  if (isOOS) {
    baseColor = '#EF4444';  // Red - Out Of Service (overrides selected state)
    strokeColor = hexToRgba(outlineHex, outlineAlpha);
  } else if (isNotWorking) {
    baseColor = '#FFFAA0';  // Light Yellow - Not Working
    strokeColor = hexToRgba(outlineHex, outlineAlpha);
  } else {
    strokeColor = hexToRgba(outlineHex, outlineAlpha);  // White (dark) / black (light) stroke with opacity
    if (hasDispatch && !isCompleted) {
      baseColor = '#3B82F6';  // Blue - In Progress
    } else if ((remainingDispatches || 0) > 0 && !hasOriginArrivalTime) {
      baseColor = '#F59E0B';  // Orange - Not Started (dispatched but hasn't started next dispatch)
    } else if (hasDispatch && isCompleted && (remainingDispatches || 0) > 0) {
      baseColor = '#6B7280';  // Grey - Dispatched (has more work to do and has started)
    } else {
      baseColor = '#10B981';  // Green - Available (no dispatch or done for the day)
    }
  }

  // Apply opacity to the color
  const color = hexToRgba(baseColor, opacity);

  // Set size for truck icons
  // TruckEmpty is smaller (32px) to better match visual scale
  // TruckWithContainer and TruckWithChassis are 60px
  // Apply size multiplier from slider
  const baseSize = truckType === 'TruckEmpty' ? 32 : truckType === 'Mule' ? 32 : 60;
  const size = Math.round(baseSize * sizeMultiplier);

  const container = document.createElement('div');
  container.style.width = `${size}px`;
  container.style.height = `${size}px`;
  // Center the marker on the position point
  container.style.transform = 'translate(-50%, -50%)';
  container.style.position = 'absolute';
  container.style.cursor = 'pointer';

  // Calculate rotation based on heading
  // Truck SVG naturally faces right (east = 90°)
  // For stopped trucks (speed <= 5), default to facing right (0° rotation)
  // For moving trucks, rotate based on heading but keep wheels down
  let rotation: number;
  let scaleY = 1;

  if (speed <= 5) {
    // Stopped truck - face right
    rotation = 0;
    scaleY = 1;
  } else {
    // Moving truck - use heading
    const actualHeading = heading !== undefined && heading !== null ? heading : 90;

    // Calculate base rotation (truck faces right at 0°)
    rotation = actualHeading - 90;

    // Normalize rotation to keep truck right-side up
    // Instead of flipping, we adjust rotation to stay within -90° to 90° range
    if (rotation > 90) {
      scaleY = -1; // Use left-facing version for TruckWithContainer
      rotation = rotation - 180;
    } else if (rotation < -90) {
      scaleY = -1; // Use left-facing version for TruckWithContainer
      rotation = rotation + 180;
    } else {
      scaleY = 1; // Normal orientation
    }
  }

  // Use different truck icons based on truck type
  switch (truckType) {
    case 'TruckWithContainer':
      // Truck with container (full trailer with cargo box)
      // Use different SVG based on whether truck needs to be flipped (to keep text readable)
      if (scaleY === -1) {
        // Use left-to-right version (pre-flipped) when heading west (180-360 degrees)
        container.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="${size}" height="${size}">
            <g transform="translate(31, 9) rotate(${rotation}) translate(-31, -9)">
              <g transform="translate(-1.4, -22.39)">
                <g transform="matrix(-1,0,0,1,64.0127,0.263741)">
                  <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="matrix(-1,0,0,1,64.0337,0.296808)">
                  <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="translate(41.1, 24.9078)">
                  <text x="0px" y="7.756px" style="font-family:var(--app-font-family);font-size:8px;fill:white;text-anchor:middle;">${escapeHtml(unitNumber)}</text>
                </g>
              </g>
            </g>
          </svg>
        `;
      } else {
        // Use normal right-facing version
        container.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="${size}" height="${size}">
            <g transform="translate(31, 9) rotate(${rotation}) translate(-31, -9)">
              <g transform="translate(-1.4, -22.39)">
                <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
                  fill="${color}"
                  stroke="${strokeColor}"
                  strokeWidth="0.4"/>
                <g transform="translate(0.033063, 0.0104117)">
                  <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                    fill="${color}"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="translate(1.68205, 24.8414)">
                  <text x="22px" y="7.756px" style="font-family:var(--app-font-family);font-size:8px;fill:white;text-anchor:middle;">${escapeHtml(unitNumber)}</text>
                </g>
              </g>
            </g>
          </svg>
        `;
      }
      break;

    case 'TruckWithChassis':
      // Truck with chassis (flatbed trailer with framework, no container)
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 15" width="${size}" height="${size}">
          <g transform="translate(31, 7.5) rotate(${rotation}) translate(-31, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 1.472,34.325 1.472,34.325C1.472,34.655 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM56.16,31.37C56.16,31.37 56.16,31.39 56.17,31.4L53.6,31.4L53.6,30.6L55.77,30.6C55.92,30.85 56.05,31.11 56.16,31.37ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-1.47155, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;

    case 'TruckEmpty':
      // Empty truck (just the cab, no trailer)
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 15" width="${size}" height="${size}">
          <g transform="translate(14.5, 7.5) rotate(${rotation}) translate(-14.5, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-34.3558, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;

    case 'Mule':
      // Mule vehicle - currently using TruckEmpty SVG as placeholder
      // TODO: Replace the SVG below with a custom mule/yard truck icon
      container.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 15" width="${size}" height="${size}">
          <g transform="translate(14.5, 7.5) rotate(${rotation}) translate(-14.5, -7.5)">
            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
              transform="translate(-34.3558, -25.4004)"
              fill="${color}"
              stroke="${strokeColor}"
              strokeWidth="0.4"/>
          </g>
        </svg>
      `;
      break;
  }

  return container;
};

// Easter egg: donkey icon for mule drivers after 30s hover
const createMuleEasterEggIcon = (color: string, size: number): HTMLElement => {
  const container = document.createElement('div');
  container.style.width = `${size}px`;
  container.style.height = `${size}px`;
  container.style.transform = 'translate(-50%, -50%)';
  container.style.position = 'absolute';
  container.style.cursor = 'pointer';
  container.innerHTML = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="${size}" height="${size}">
      <g fill="${color}">
        <path d="m104 482c-3 0-6-2-6-6l-7-56c-1-1 0-3 0-4l6-10v-1c12-17 10-56 8-72-26-38-10-87-9-89 7-19 19-33 36-40 30-13 66-1 80 5 1 1 2 1 2 1 1 0 1 1 1 1h1 17c46 1 140 3 143-39 3-25 39-32 40-33 4 0 8 2 8 6 1 4-1 7-5 8s-27 6-29 21c-5 55-104 52-157 51-7 0-12 0-17 0-3 0-5-1-8-2 0 0-1-1-1-1-10-4-44-16-70-5-13 6-22 16-28 31 0 3-15 47 8 79 1 1 1 2 2 3 0 2 8 57-10 83l-4 8 6 54c1 3-2 7-6 7 0 0 0 0-1 0z"/>
        <path d="m104 482h-28c-3 0-6-2-7-6l-7-72c0-2 0-3 1-4 0-1 10-18 20-35 6-10 10-18 14-24 2-4 3-6 5-8 2-3 4-8 10-6 3 1 5 5 4 9 0 1-1 2-2 3-3 5-24 42-38 66l6 63h22c4 0 7 4 7 7 0 4-3 7-7 7zm3-142c1 1 2 1 3 1-1 0-2 0-3-1z"/>
        <path d="m344 483h-33c-4 0-7-4-7-7v-90c0-9-3-19-6-22 0 0-1 0-2 1-9 3-27 8-48 9-24 0-44-6-62-18-6 6-16 14-26 15l-25 56 12 47c0 2 0 4-2 6-1 1-3 2-5 2h-36c-3 0-7-3-7-7 0-3 4-7 7-7h27l-10-40c0-2 0-3 0-5l28-62c1-3 4-4 7-4 6 1 19-8 25-15 2-2 6-3 9 0 37 29 87 15 101 9 6-2 11-1 15 2 11 8 12 29 12 33v83h19v-47c-8-26 13-83 16-89 0-1 1-2 2-3l21-18c1-2 3-5 5-9 28-49 34-58 38-60 14-9 29-4 43 13 8 10 20 9 27 3 9-6 13-20 3-34l-56-79c-1-1-2-3-2-5 2-27-21-66-34-87-6 53 23 87 23 88 2 3 2 7-1 9-3 3-7 3-10 0-1-2-39-48-22-117 0-3 2-5 5-5 3-1 6 0 7 3 2 2 47 64 47 108l54 77c15 21 9 43-5 53-13 10-33 11-47-6-13-16-21-11-24-10-5 6-26 42-34 56-2 4-5 8-6 10 0 0 0 1-1 1l-21 18c-6 18-21 63-15 80 1 1 1 1 1 2v55c0 2-1 4-2 5-2 2-3 2-5 2z"/>
        <path d="m376 483h-32c-4 0-7-3-7-7s3-7 7-7h25v-108c0-2 0-3 1-4 8-12 5-33 4-40 0-3 2-7 6-8 4 0 8 2 8 6 2 12 4 33-5 48v113c0 4-3 7-7 7z"/>
        <path d="m445 256c-2 0-3 0-4-1-3-3-4-7-1-10l39-48c2-3 7-3 10-1 3 3 3 7 1 10l-40 48c-1 1-3 2-5 2z"/>
        <path d="m18 375c-10 0-14-4-16-7-4-7-2-16 1-20 3-5 8-5 13-5 10-1 22-2 30-25l22-62c17-43 46-43 52-43 4 1 7 4 6 8 0 4-3 7-7 6-4 0-24 0-38 34l-22 62c-11 32-32 33-42 34-1 0-2 0-3 0 0 1 0 3 1 4 0 0 2 1 4 0h1c23-2 50-13 55-39 3-15 9-41 17-59 1-3 6-5 9-3 4 1 5 5 4 9-7 16-13 42-16 55-7 34-40 49-68 51-1 0-2 0-3 0z"/>
        <path d="m450 199h-4c-4 0-7-3-7-7s3-7 7-7h4c3 0 7 3 7 7s-4 7-7 7z"/>
        <path d="m299 364c-4 0-7-3-7-7v-46c0-4 3-7 7-7 3 0 7 3 7 7v46c0 4-4 7-7 7z"/>
        <path d="m184 356c-2 0-3-1-5-2-2-3-2-8 0-10l33-33c3-3 7-3 10 0s3 7 0 10l-33 33c-1 1-3 2-5 2z"/>
        <path d="m423 256c-1 0-1 0-2 0l-24-7c-4-1-6-5-5-8 1-4 5-6 8-5l25 6c3 1 5 5 4 9 0 3-3 5-6 5z"/>
      </g>
    </svg>
  `;
  return container;
};

// Haversine distance calculation (returns miles)
const AVERAGE_TRUCK_SPEED_MPH = 45;
const haversineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Format minutes to hours and minutes string
const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0) {
    return `${hours}h ${mins}m`;
  }
  return `${mins}m`;
};

// Cache for dispatch details to avoid repeated API calls
const dispatchDetailsCache = new Map<string, { dispatches: any[], timestamp: number }>();
const CACHE_TTL = 60000; // 1 minute cache

// Component to fetch and display dispatch details in a tooltip
interface DispatchesTooltipContentProps {
  unitNumber: string;
  type: 'done' | 'remaining';
}

const DispatchesTooltipContent: React.FC<DispatchesTooltipContentProps> = ({ unitNumber, type }) => {
  const [dispatches, setDispatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDispatches = async () => {
      // Check cache first
      const cached = dispatchDetailsCache.get(unitNumber);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setDispatches(cached.dispatches);
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/driver-dispatch/driver-current-dispatches/${unitNumber}`);
        const data = response.data?.dispatches || [];
        dispatchDetailsCache.set(unitNumber, { dispatches: data, timestamp: Date.now() });
        setDispatches(data);
      } catch (err) {
        setError('Failed to load');
      } finally {
        setLoading(false);
      }
    };

    fetchDispatches();
  }, [unitNumber]);

  if (loading) {
    return <span className="text-xs text-gray-500">Loading...</span>;
  }

  if (error) {
    return <span className="text-xs text-red-500">{error}</span>;
  }

  const filteredDispatches = type === 'done'
    ? dispatches.filter(d => d.isComplete)
    : dispatches.filter(d => !d.isComplete);

  if (filteredDispatches.length === 0) {
    return <span className="text-xs text-gray-500">No {type} dispatches</span>;
  }

  return (
    <div className="text-xs max-w-xs">
      <div className="font-semibold mb-1 text-label">
        {type === 'done' ? 'Completed' : 'Remaining'} Dispatches:
      </div>
      <div className="space-y-1.5">
        {filteredDispatches.map((d, idx) => (
          <div key={idx} className="border-l-2 border-gray-300 dark:border-gray-600 pl-2">
            <div className="font-medium text-heading">{d.pronumber}</div>
            <div className="text-gray-600 dark:text-gray-400 truncate" style={{ maxWidth: '200px' }}>
              {d.originName} → {d.destName}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const LiveMap: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { setIconSizeSlider, setGlobalFilters } = useDispatchFooter();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { accounts } = useMsal();
  const { permissions } = usePermissions();
  const [driverLocations, setDriverLocations] = useState<VehicleLocationResponse[]>([]);
  const [pendingDriverSelect, setPendingDriverSelect] = useState<string | null>(null);
  const [driverDispatches, setDriverDispatches] = useState<DriverDispatchResponse[]>([]);
  const [detailedDispatchInfo, setDetailedDispatchInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(null);
  const [hoveredVehicle, setHoveredVehicle] = useState<{
    id: string;
    position: { x: number; y: number };
    info: {
      unitNumber?: string;
      driverName?: string;
      proNumber?: string;
      completedToday: number;
      remainingDispatches: number;
      isOOS: boolean;
      isNotWorking: boolean;
      // Endorsements
      has93k?: boolean;
      hasHazmat?: boolean;
      hasTank?: boolean;
      isLightTruck?: boolean;
      isDaycab?: boolean;
      isMule?: boolean;
    };
  } | null>(null);
  const [isInfoWindowMinimized, setIsInfoWindowMinimized] = useState(false);
  const [showMobileVehicles, setShowMobileVehicles] = useState(false);
  const [showDesktopSidebar, setShowDesktopSidebar] = useState(true);
  const [isVehicleInfoDrawerCollapsed, setIsVehicleInfoDrawerCollapsed] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [isListLocked, setIsListLocked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme === 'dark' || (!savedTheme && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  const [feedServiceActive, setFeedServiceActive] = useState(false);
  const [feedServiceLastUpdate, setFeedServiceLastUpdate] = useState<Date | null>(null);
  const [currentDispatches, setCurrentDispatches] = useState<any>(null);
  const [loadingDispatches, setLoadingDispatches] = useState(false);
  const [driverMovesData, setDriverMovesData] = useState<DriverWithMoves | null>(null);
  const [loadingDriverMoves, setLoadingDriverMoves] = useState(false);
  const [showCompletedMoves, setShowCompletedMoves] = useState(false);
  // Initialize icon size from localStorage if available, otherwise default to 1.0
  const [iconSizeMultiplier, setIconSizeMultiplier] = useState<number>(() => {
    const savedSize = localStorage.getItem('live_map_icon_size');
    if (savedSize) {
      const size = parseFloat(savedSize);
      if (!isNaN(size)) {
        return size;
      }
    }
    return 1; // 0.5 to 2.0, default 1.0
  });

  // Store legacy CMS URL for use in render functions
  const legacyCmsUrl = config.LEGACY_CMS_URL;

  // Achievement notification state
  const [showAchievementNotification, setShowAchievementNotification] = useState(false);
  const [achievementMessage, setAchievementMessage] = useState<{ title: string; id: string } | null>(null);

  // Mule easter egg: hover over a mule driver for 30 seconds to reveal a donkey
  const muleHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const muleEasterEggVehiclesRef = useRef<Set<string>>(new Set());

  // Filter checkboxes - all unchecked by default
  const [showInProgress, setShowInProgress] = useState(false);
  const [showAvailable, setShowAvailable] = useState(false);
  const [showDispatched, setShowDispatched] = useState(false);
  const [showOutOfService, setShowOutOfService] = useState(false);
  const [selectedDispatchers, setSelectedDispatchers] = useState<string[]>([]);
  // Region + Local/OTR header filters
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [selectedDriverTypes, setSelectedDriverTypes] = useState<DriverType[]>([]);
  const [mapReady, setMapReady] = useState(false);
  // Endorsement filters (for footer badges)
  const [show93k, setShow93k] = useState(false);
  const [showHazmat, setShowHazmat] = useState(false);
  const [showTank, setShowTank] = useState(false);
  const [showLightTruck, setShowLightTruck] = useState(false);
  const [showDaycab, setShowDaycab] = useState(false);
  const [showSleeper, setShowSleeper] = useState(false);
  // Popup visibility state
  const [showEndorsementsPopup, setShowEndorsementsPopup] = useState(false);
  const [showEquipTypePopup, setShowEquipTypePopup] = useState(false);
  // Equipment type filters (for footer badges)
  const [selectedSizes, setSelectedSizes] = useState<string[]>([]);
  const [selectedEquipTypes, setSelectedEquipTypes] = useState<string[]>([]);
  const [selectedChassisTypes, setSelectedChassisTypes] = useState<string[]>([]);
  const [mapBounds, setMapBounds] = useState<google.maps.LatLngBounds | null>(null);
  // Initialize map type from localStorage if available, otherwise default to 'roadmap'
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>(() => {
    const savedMapType = localStorage.getItem('live_map_type');
    if (savedMapType === 'roadmap' || savedMapType === 'hybrid') {
      return savedMapType;
    }
    return 'roadmap';
  });

  const mapRef = useRef<google.maps.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const clustererRef = useRef<MarkerClusterer | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const setHoveredVehicleRef = useRef(setHoveredVehicle);
  setHoveredVehicleRef.current = setHoveredVehicle; // Keep ref up to date
  const directionsRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destinationMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const lastDispatchFetchRef = useRef<string | null>(null);
  const iconSizeLoadedRef = useRef(false); // Track if icon size preference has been loaded
  const mapTypeLoadedRef = useRef(false); // Track if map type preference has been loaded
  const mapTypeControlRef = useRef<HTMLButtonElement | null>(null); // Ref for map type toggle control
  const homeRegionPannedRef = useRef(false); // Track if we've panned to user's home region
  const [routeInfo, setRouteInfo] = useState<{ distance: string; duration: string; eta?: string; } | null>(null);

  // Refs and state for clicked dispatch routes (not-started/future dispatches)
  const clickedDispatchRouteRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const clickedDispatchTraveledRouteRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);
  const clickedDispatchOriginMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const clickedDispatchDestMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const clickedDispatchIntermediateMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const clickedDispatchGeofencesRef = useRef<(google.maps.Circle | google.maps.Polygon)[]>([]);
  const parkingLocationCircleRef = useRef<google.maps.Circle | null>(null);
  const parkingLocationMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const [selectedDispatchRoute, setSelectedDispatchRoute] = useState<{
    proNumber: string;
    isInProgress: boolean;
    isCompleted: boolean;
    skipZoom?: boolean;
    legs: Array<{
      origcoordinates: string;
      destcoordinates: string;
      originName: string;
      destName: string;
      origgeofencetype: string;
      origgeofenceradius: number | string;
      destgeofencetype: string;
      destgeofenceradius: number | string;
      isComplete?: boolean;
    }>;
  } | null>(null);

  // Available pros state and refs
  const [availablePros, setAvailablePros] = useState<any[]>([]);
  const availableProMarkersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);
  const availableProClustersRef = useRef<any[][]>([]);
  const lastVisibleProNumbersRef = useRef<string>('');
  const [selectedProClusterList, setSelectedProClusterList] = useState<any[][] | null>(null);
  const [visibleProClusters, setVisibleProClusters] = useState<any[][]>([]);
  const [isViewingAllVisible, setIsViewingAllVisible] = useState(false);
  const [isProDrawerOpen, setIsProDrawerOpen] = useState(false);
  const [showProSearch, setShowProSearch] = useState(false);
  const [proSearchQuery, setProSearchQuery] = useState('');

  // Location search state
  const [locationSearchQuery, setLocationSearchQuery] = useState('');
  const locationSearchInputRef = useRef<HTMLInputElement | null>(null);
  const locationSearchMobileInputRef = useRef<HTMLInputElement | null>(null);
  const locationSearchAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const locationSearchMobileAutocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const locationSearchMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);

  // Pro details modal state
  const [isProDetailsModalOpen, setIsProDetailsModalOpen] = useState(false);
  const [selectedProNumber, setSelectedProNumber] = useState<number | null>(null);


  // Load Google Maps
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsLoaderOptions());


  // Ensure theme is applied on mount and track dark mode
  // Initialize component on mount: theme, icon size, and initial data fetch
  useEffect(() => {
    // Watch for theme changes (initial value already set in useState)
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Load icon size preference from localStorage (already loaded on login)
    const savedIconSize = localStorage.getItem('live_map_icon_size');

    if (savedIconSize) {
      const size = parseFloat(savedIconSize);
      if (!isNaN(size)) {
        setIconSizeMultiplier(size);
      }
      iconSizeLoadedRef.current = true;
    } else {
      // Fallback: if localStorage isn't set yet, load from API
      const loadIconSizePreference = async () => {
        try {
          const setting = await userSettingsService.getUserSetting('live_map_icon_size');
          if (setting && setting.setting_value && typeof setting.setting_value.size === 'number') {
            setIconSizeMultiplier(setting.setting_value.size);
            localStorage.setItem('live_map_icon_size', setting.setting_value.size.toString());
          }
        } catch (error: any) {
          // Only log non-404 errors (404 is expected for new users)
          if (error?.response?.status !== 404) {
            console.error('Failed to load icon size preference:', error);
          }
        } finally {
          iconSizeLoadedRef.current = true;
        }
      };
      loadIconSizePreference();
    }

    // Load map type preference from localStorage (already loaded on login)
    const savedMapType = localStorage.getItem('live_map_type');

    if (savedMapType === 'roadmap' || savedMapType === 'hybrid') {
      setMapType(savedMapType);
      mapTypeLoadedRef.current = true;
    } else {
      // Fallback: if localStorage isn't set yet, load from API
      const loadMapTypePreference = async () => {
        try {
          const setting = await userSettingsService.getUserSetting('live_map_type');
          if (setting && setting.setting_value && (setting.setting_value.type === 'roadmap' || setting.setting_value.type === 'hybrid')) {
            setMapType(setting.setting_value.type);
            localStorage.setItem('live_map_type', setting.setting_value.type);
          }
        } catch (error: any) {
          // Only log non-404 errors (404 is expected for new users)
          if (error?.response?.status !== 404) {
            console.error('Failed to load map type preference:', error);
          }
        } finally {
          mapTypeLoadedRef.current = true;
        }
      };
      loadMapTypePreference();
    }

    // Fetch initial data
    fetchData();
    fetchFeedStatus();

    return () => observer.disconnect();
  }, []);

  // Pan to user's home region on initial map load
  useEffect(() => {
    // Only run once when map is ready and we have permissions
    if (!mapReady || !mapRef.current || homeRegionPannedRef.current) return;

    const homeRegion = permissions?.home_region_as_default !== false ? permissions?.home_region : undefined;
    const regionCoords = getRegionCoordinates(homeRegion);

    if (regionCoords) {
      // Pan to user's home region
      mapRef.current.panTo({ lat: regionCoords.lat, lng: regionCoords.lng });
      mapRef.current.setZoom(regionCoords.zoom);
      homeRegionPannedRef.current = true;
    } else {
      // No home region set, mark as done so we don't keep checking
      homeRegionPannedRef.current = true;
    }
  }, [mapReady, permissions?.home_region, permissions?.home_region_as_default]);

  const fetchFeedStatus = useCallback(async () => {
    try {
      const response = await api.get('/samsara/location-feed/status');
      const result = response.data;
      setFeedServiceActive(result.is_active || false);
      if (result.last_update_time) {
        setFeedServiceLastUpdate(new Date(result.last_update_time));
      }
    } catch (err: any) {
      // Silently handle feed status errors
    }
  }, []);

  // Save icon size preference when it changes (but not on initial load)
  useEffect(() => {
    // Skip saving if we haven't loaded the initial preference yet
    if (!iconSizeLoadedRef.current) {
      return;
    }

    // Update localStorage immediately for instant access (synchronous)
    localStorage.setItem('live_map_icon_size', iconSizeMultiplier.toString());

    // Also save to database for persistence across devices/sessions (async)
    const saveIconSizePreference = async () => {
      try {
        await userSettingsService.saveUserSetting('live_map_icon_size', { size: iconSizeMultiplier });
      } catch (error) {
        console.error('Failed to save icon size preference:', error);
      }
    };

    saveIconSizePreference();
  }, [iconSizeMultiplier]);

  // Save map type preference when it changes (but not on initial load)
  useEffect(() => {
    // Skip saving if we haven't loaded the initial preference yet
    if (!mapTypeLoadedRef.current) {
      return;
    }

    // Update localStorage immediately for instant access (synchronous)
    localStorage.setItem('live_map_type', mapType);

    // Also save to database for persistence across devices/sessions (async)
    const saveMapTypePreference = async () => {
      try {
        await userSettingsService.saveUserSetting('live_map_type', { type: mapType });
      } catch (error) {
        console.error('Failed to save map type preference:', error);
      }
    };

    saveMapTypePreference();
  }, [mapType]);

  // Update map type dynamically without remounting (preserves zoom, position, and markers)
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setMapTypeId(mapType);
    }
    // Update the control button icon
    if (mapTypeControlRef.current) {
      const iconStroke = isDarkMode ? '#e5e7eb' : '#555';
      const globeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="1.5" style="width: 24px; height: 24px;"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>`;
      const mapIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="1.5" style="width: 24px; height: 24px;"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" /><path d="M9 3v15" /><path d="M15 6v15" /></svg>`;
      mapTypeControlRef.current.innerHTML = mapType === 'roadmap' ? globeIcon : mapIcon;
      mapTypeControlRef.current.title = mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view';
    }
  }, [mapType, isDarkMode]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch driver locations from FastCMS database instead of Samsara API
      // Fetch locations from cache_vehicle_location (includes dispatch data)
      const locationsResponse = await api.get('/dispatching/driver-locations', {
        params: { minutes_back: 15 }
      });

      const locations = locationsResponse.data?.data || [];
      setDriverLocations(locations);

      // No longer need separate dispatch fetch - dispatch data is included in locations
      setDriverDispatches([]); // Clear dispatches since we're using embedded data

      setLastUpdate(new Date());
      setError(null);
      setLoading(false);

    } catch (err: any) {
      setError(err.message || 'Failed to fetch fleet data');
      setLoading(false);
    }
  }, []);

  // Clear selected vehicle and adjust map view when dispatcher filter changes
  useEffect(() => {
    setSelectedVehicleId(null);

    if (!mapRef.current) return;

    // If no dispatchers selected or "All Dispatchers" selected, center on Houston
    if (selectedDispatchers.length === 0) {
      mapRef.current.panTo(defaultCenter);
      mapRef.current.setZoom(11);
      return;
    }

    // If dispatchers are selected, fit bounds to show all their vehicles
    // Note: vehicleMarkers is accessed directly here since it's a computed value
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    if (vehicleMarkers.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      vehicleMarkers.forEach(vehicle => {
        bounds.extend({ lat: vehicle.latitude, lng: vehicle.longitude });
      });
      mapRef.current.fitBounds(bounds);
    }
  }, [selectedDispatchers]);

  // Background refresh every 60 seconds (full vehicle list refresh)
  useVisibilityAwareInterval(fetchData, 60000);

  // Check feed status every 30 seconds
  useVisibilityAwareInterval(fetchFeedStatus, 30000);

  // Fetch current dispatches when vehicle selection changes
  // We fetch once per vehicle selection and keep the data until a different vehicle is selected
  useEffect(() => {
    // Reset dispatches when no vehicle is selected
    if (!selectedVehicleId) {
      setCurrentDispatches(null);
      setLoadingDispatches(false);
      lastDispatchFetchRef.current = null;
      // Clear available PRO markers when vehicle is unselected
      availableProMarkersRef.current.forEach(marker => {
        marker.map = null;
      });
      availableProMarkersRef.current = [];
      setAvailablePros([]);
      setIsProDrawerOpen(false); // Close Available Pros drawer
      setSelectedProClusterList(null); // Clear selected PRO cluster
      setIsViewingAllVisible(false); // Reset viewing all flag
      // Clear parking location marker and circle
      if (parkingLocationCircleRef.current) {
        parkingLocationCircleRef.current.setMap(null);
        parkingLocationCircleRef.current = null;
      }
      if (parkingLocationMarkerRef.current) {
        parkingLocationMarkerRef.current.map = null;
        parkingLocationMarkerRef.current = null;
      }
      return;
    }

    // Check if we've already fetched dispatches for this vehicle
    if (lastDispatchFetchRef.current === selectedVehicleId) {
      return; // Already fetched for this vehicle, keep existing data
    }

    // Clear old dispatches immediately when switching to a new vehicle
    setCurrentDispatches(null);
    setLoadingDispatches(true);

    // Find the selected vehicle to get its unit number
    const selectedVehicle = driverLocations.find(v => v.vehicleId === selectedVehicleId);

    // If vehicle not found in current data, wait for it
    if (!selectedVehicle || !selectedVehicle.unitNumber) {
      // Vehicle data not ready yet, keep loading state
      // We'll retry on the next driverLocations update
      return;
    }

    // Mark that we're fetching for this vehicle (after we have the unit number)
    lastDispatchFetchRef.current = selectedVehicleId;
    unitsService.getDriverCurrentDispatches(selectedVehicle.unitNumber)
      .then(response => {
        // Only update if still the same selected vehicle
        if (selectedVehicleId === lastDispatchFetchRef.current) {
          setCurrentDispatches(response.data);
          setLoadingDispatches(false);
        }
      })
      .catch(error => {
        console.error('Error fetching current dispatches:', error);
        if (selectedVehicleId === lastDispatchFetchRef.current) {
          setLoadingDispatches(false);
          // Still set to null on error, but only for the current vehicle
          setCurrentDispatches(null);
        }
      });
  }, [selectedVehicleId, driverLocations]); // Include driverLocations to get fresh vehicle data when it loads

  // Fetch DriverWithMoves data for DriverLane display
  useEffect(() => {
    let cancelled = false;

    if (!selectedVehicleId) {
      setDriverMovesData(null);
      return;
    }

    const selectedVehicle = driverLocations.find(v => v.vehicleId === selectedVehicleId);
    if (!selectedVehicle || !selectedVehicle.unitNumber) return;

    setLoadingDriverMoves(true);
    api.get(`/driver-status-monitor/drivers/${selectedVehicle.unitNumber}`)
      .then(response => {
        if (!cancelled) {
          setDriverMovesData(response.data);
        }
      })
      .catch(error => {
        console.error('Error fetching driver moves:', error);
        if (!cancelled) {
          setDriverMovesData(null);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDriverMoves(false);
        }
      });

    return () => { cancelled = true; };
  }, [selectedVehicleId, driverLocations]);

  // Process vehicle locations into markers
  // Location data now includes embedded dispatch info from cache_vehicle_location
  const allVehicleMarkers: VehicleMarker[] = useMemo(() => driverLocations.map(loc => {
      return {
        id: loc.vehicleId,
        uniqueKey: loc.vehicleId,
        name: loc.vehicleName,
        latitude: loc.latitude || 0,
        longitude: loc.longitude || 0,
        speedMilesPerHour: loc.speed || 0,
        headingDegrees: loc.heading,
        vin: loc.vin,
        // Use dispatch info directly from cache (no need to join)
        dispatchInfo: (loc.driverName || loc.proNumber) ? {
          driverName: loc.driverName || null,
          unitNumber: loc.unitNumber || null,
          dispatcher: loc.dispatcher || null,
          dispatcherDisplayName: loc.dispatcherDisplayName || null,
          region: loc.region || null,
          proNumber: loc.proNumber || null,
          originName: loc.originName || null,
          originCoordinates: loc.originCoordinates || null,
          originArrivalDateTime: loc.originArrivalDateTime && loc.originArrivalDateTime !== '0000-00-00 00:00:00' ? loc.originArrivalDateTime : null,
          originDepartDateTime: loc.originDepartDateTime && loc.originDepartDateTime !== '0000-00-00 00:00:00' ? loc.originDepartDateTime : null,
          destinationName: loc.destinationName || null,
          destinationCoordinates: loc.destinationCoordinates || null,
          arrivalDateTime: loc.arrivalDateTime && loc.arrivalDateTime !== '0000-00-00 00:00:00' ? loc.arrivalDateTime : null,
          departDateTime: loc.departDateTime && loc.departDateTime !== '0000-00-00 00:00:00' ? loc.departDateTime : null,
          isCompleted: loc.dispatchCompleted || false,
          remainingDispatchesToday: loc.remainingDispatchesToday || 0,
          isBareChassis: loc.isBareChassis || false,
          v1Status: loc.v1Status || null,
          driverStatus: loc.driverStatus || null,
          // Endorsement data
          isDaycab: loc.isDaycab || false,
          hasHazmat: loc.hasHazmat || false,
          hasTank: loc.hasTank || false,
          has93k: loc.has93k || false,
          isLightTruck: loc.isLightTruck || false,
          isMule: loc.isMule || false,
          preference: loc.preference || null,
          // Equipment type data
          size: loc.size || undefined,
          equipType: loc.equipType || undefined,
          chassisType: loc.chassisType || undefined,
        } : null,
      };
    }), [driverLocations]);

  // Dispatcher options state (fetched from backend)
  const [driverDispatcherOptions, setDriverDispatcherOptions] = useState<TypeAheadOption[]>([
    { value: 'all', label: 'All Driver Dispatchers' }
  ]);

  // Fetch driver dispatchers from backend
  useEffect(() => {
    const fetchDriverDispatchers = async () => {
      try {
        const response = await ordersService.getDriverDispatchers();
        const options: TypeAheadOption[] = [
          { value: 'all', label: 'All Driver Dispatchers' },
          ...response.data.map((d: any) => ({
            value: d.username,
            label: d.displayname
          }))
        ];
        setDriverDispatcherOptions(options);
      } catch (err) {
        console.error('Failed to fetch driver dispatchers:', err);
      }
    };
    fetchDriverDispatchers();
  }, []);

  // Use driverDispatcherOptions for the dropdown
  const dispatcherOptions = driverDispatcherOptions;

  // Region options come from the regions actually present on the loaded vehicles
  const regionOptions = useMemo<TypeAheadOption[]>(
    () => buildRegionOptions(allVehicleMarkers.map(v => v.dispatchInfo?.region)),
    [allVehicleMarkers]
  );

  const handleRegionChange = (value: string | string[]) => {
    setSelectedRegions(Array.isArray(value) ? value : [value]);
  };

  const toggleDriverType = (type: DriverType) => {
    setSelectedDriverTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  };

  // Handler for dispatcher selection - "All Dispatchers" clears all selections
  const handleDispatcherChange = (value: string | string[]) => {
    const newValues = Array.isArray(value) ? value : [value];

    // If "All Dispatchers" was just selected, clear all selections
    if (newValues.includes('all') && !selectedDispatchers.includes('all')) {
      setSelectedDispatchers([]);
    }
    // If any other dispatcher is selected while "All" is selected, remove "All"
    else if (newValues.includes('all') && newValues.length > 1) {
      setSelectedDispatchers(newValues.filter(v => v !== 'all'));
    }
    // If "All" is being deselected, just update normally
    else {
      setSelectedDispatchers(newValues.filter(v => v !== 'all'));
    }
  };

  // Helper function to format dispatcher display text (responsive)
  const getDispatcherDisplayText = (limit?: number) => {
    if (selectedDispatchers.length === 0) {
      return 'All Dispatchers';
    }

    if (limit && selectedDispatchers.length > limit) {
      const shown = selectedDispatchers.slice(0, limit);
      const remaining = selectedDispatchers.length - limit;
      return `${shown.join(', ')} & ${remaining} ${remaining === 1 ? 'Other' : 'Others'}`;
    }

    return selectedDispatchers.join(', ');
  };

  // Filter vehicles based on search query and dispatch status checkboxes
  const vehicleMarkers = useMemo(() => allVehicleMarkers.filter(vehicle => {
    // Always include the selected vehicle, even if it doesn't match filters
    if (selectedVehicleId && vehicle.id === selectedVehicleId) {
      return true;
    }

    // Filter by search query if present
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = (
        vehicle.dispatchInfo?.driverName?.toLowerCase().includes(query) ||
        vehicle.dispatchInfo?.unitNumber?.toLowerCase().includes(query)
      );
      if (!matchesSearch) return false;
    }

    // Filter by driver dispatchers if any are selected (uses dispatcher username)
    if (selectedDispatchers.length > 0) {
      const vehicleDispatcher = vehicle.dispatchInfo?.dispatcher;
      if (!vehicleDispatcher || !selectedDispatchers.includes(vehicleDispatcher)) {
        return false;
      }
    }

    // Filter by region (OR within selected regions; "No Region" bucket for blanks)
    if (!matchesRegion(vehicle.dispatchInfo?.region, selectedRegions)) {
      return false;
    }

    // Filter by Local / OTR driver preference (none or both selected = show all)
    if (!matchesDriverType(vehicle.dispatchInfo?.preference, selectedDriverTypes)) {
      return false;
    }

    // Filter by endorsements (AND logic - show if vehicle has ALL selected endorsements)
    const anyEndorsementFilters = show93k || showHazmat || showTank || showLightTruck || showDaycab || showSleeper;

    if (anyEndorsementFilters) {
      // Check all endorsements the vehicle has (a truck can have multiple)
      const vehicleHas93k = vehicle.dispatchInfo?.has93k || false;
      const vehicleHasHazmat = vehicle.dispatchInfo?.hasHazmat || false;
      const vehicleHasTank = vehicle.dispatchInfo?.hasTank || false;
      const vehicleIsLightTruck = vehicle.dispatchInfo?.isLightTruck || false;
      const vehicleIsDaycab = vehicle.dispatchInfo?.isDaycab || false;
      const vehicleIsSleeper = !vehicleIsDaycab;

      // Vehicle must have ALL selected endorsements (AND logic)
      if (show93k && !vehicleHas93k) return false;
      if (showHazmat && !vehicleHasHazmat) return false;
      if (showTank && !vehicleHasTank) return false;
      if (showLightTruck && !vehicleIsLightTruck) return false;
      if (showDaycab && !vehicleIsDaycab) return false;
      if (showSleeper && !vehicleIsSleeper) return false;
    }

    // Filter by equipment type (AND logic - must match ALL selected filters)
    const anyEquipTypeFilters = selectedSizes.length > 0 || selectedEquipTypes.length > 0 || selectedChassisTypes.length > 0;

    if (anyEquipTypeFilters) {
      const vehicleSize = vehicle.dispatchInfo?.size || '';
      const vehicleEquipType = vehicle.dispatchInfo?.equipType || '';
      const vehicleChassisType = vehicle.dispatchInfo?.chassisType || '';

      // Vehicle must match at least one of each selected filter type (OR within, AND between)
      if (selectedSizes.length > 0 && !selectedSizes.includes(vehicleSize)) return false;
      if (selectedEquipTypes.length > 0 && !selectedEquipTypes.includes(vehicleEquipType)) return false;
      if (selectedChassisTypes.length > 0 && !selectedChassisTypes.includes(vehicleChassisType)) return false;
    }

    // Filter by dispatch status based on toggle buttons
    // If none selected OR all selected, show all trucks
    const noneSelected = !showInProgress && !showDispatched && !showAvailable && !showOutOfService;
    const allSelected = showInProgress && showDispatched && showAvailable && showOutOfService;

    if (noneSelected || allSelected) {
      return true; // Show all trucks
    }

    // Otherwise, filter based on which buttons are selected
    const hasDispatch = vehicle.dispatchInfo?.proNumber;
    const isCompleted = vehicle.dispatchInfo?.isCompleted;
    const remainingDispatches = vehicle.dispatchInfo?.remainingDispatchesToday || 0;
    const isOOS = vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE';

    // Determine status:
    // - Out Of Service: Driver status is OUT OF SERVICE (highest priority)
    // - In Progress: Has dispatch and current dispatch is NOT completed
    // - Dispatched: Has remaining dispatches (includes both "Not Started" and "Dispatched" statuses)
    // - Available: No dispatch OR completed with 0 remaining
    if (isOOS) {
      // Out of service
      return showOutOfService;
    } else if (hasDispatch && !isCompleted) {
      // In Progress (actively working on current dispatch)
      return showInProgress;
    } else if (remainingDispatches > 0) {
      // Dispatched (includes both "Not Started" and trucks that have started)
      return showDispatched;
    } else {
      // No dispatch - show as Available by default
      return showAvailable;
    }
  }).sort((a, b) => {
    // Sort by unit number
    const unitA = a.dispatchInfo?.unitNumber || '';
    const unitB = b.dispatchInfo?.unitNumber || '';

    // Handle numeric unit numbers
    const numA = parseInt(unitA, 10);
    const numB = parseInt(unitB, 10);

    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB;
    }

    // Fallback to string comparison for non-numeric unit numbers
    return unitA.localeCompare(unitB);
  }), [allVehicleMarkers, selectedVehicleId, searchQuery, selectedDispatchers, selectedRegions, selectedDriverTypes, showInProgress, showDispatched, showAvailable, showOutOfService, show93k, showHazmat, showTank, showLightTruck, showDaycab, showSleeper, selectedSizes, selectedEquipTypes, selectedChassisTypes]);

  // Split vehicles into "in view" and "outside view" based on map bounds
  const vehiclesInView = useMemo(() => {
    // If list is locked, show all vehicles regardless of map bounds
    if (isListLocked) return vehicleMarkers;
    if (!mapBounds) return vehicleMarkers;
    return vehicleMarkers.filter(vehicle => {
      const latLng = new google.maps.LatLng(vehicle.latitude, vehicle.longitude);
      return mapBounds.contains(latLng);
    });
  }, [vehicleMarkers, mapBounds, isListLocked]);

  const vehiclesOutsideView = useMemo(() => {
    // If list is locked, don't show outside view section
    if (isListLocked) return [];
    if (!mapBounds) return [];
    return vehicleMarkers.filter(vehicle => {
      const latLng = new google.maps.LatLng(vehicle.latitude, vehicle.longitude);
      return !mapBounds.contains(latLng);
    });
  }, [vehicleMarkers, mapBounds, isListLocked]);

  // Handle URL parameter for driver selection (from global search)
  useEffect(() => {
    const driverParam = searchParams.get('driver');
    if (driverParam) {
      setPendingDriverSelect(driverParam);
      // Clear the URL param
      searchParams.delete('driver');
      setSearchParams(searchParams, { replace: true });
    }
  }, []);

  // Select driver when data loads and we have a pending selection
  useEffect(() => {
    if (!pendingDriverSelect || allVehicleMarkers.length === 0) return;

    const vehicle = allVehicleMarkers.find(
      v => v.dispatchInfo?.unitNumber === pendingDriverSelect
    );

    if (vehicle) {
      setSelectedVehicleId(vehicle.id);
      // Pan to the vehicle
      if (mapRef.current) {
        mapRef.current.panTo({ lat: vehicle.latitude, lng: vehicle.longitude });
        mapRef.current.setZoom(12);
      }
      setPendingDriverSelect(null);
    }
  }, [pendingDriverSelect, allVehicleMarkers]);

  // Statistics - calculated from ALL vehicles, not filtered ones
  const stats = {
    totalActive: allVehicleMarkers.length,
    inProgress: allVehicleMarkers.filter(v => v.dispatchInfo?.proNumber && !v.dispatchInfo?.isCompleted).length,
    dispatched: allVehicleMarkers.filter(v => {
      const hasDispatch = v.dispatchInfo?.proNumber;
      const isCompleted = v.dispatchInfo?.isCompleted;
      const remainingDispatches = v.dispatchInfo?.remainingDispatchesToday || 0;
      const isOOS = v.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE';
      // Dispatched: has remaining dispatches (includes both "Not Started" and started trucks) and NOT OOS
      return remainingDispatches > 0 && (!hasDispatch || isCompleted) && !isOOS;
    }).length,
    available: allVehicleMarkers.filter(v => {
      const hasDispatch = v.dispatchInfo?.proNumber;
      const isCompleted = v.dispatchInfo?.isCompleted;
      const remainingDispatches = v.dispatchInfo?.remainingDispatchesToday || 0;
      const isOOS = v.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE';
      // Available: no active dispatch and no remaining dispatches and NOT OOS
      return (!hasDispatch || isCompleted) && remainingDispatches === 0 && !isOOS;
    }).length,
    outOfService: allVehicleMarkers.filter(v => v.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE').length,
  };

  // Compute available equipment type options from all vehicles
  const equipTypeOptions = useMemo(() => {
    const sizes = new Set<string>();
    const equipTypes = new Set<string>();
    const chassisTypes = new Set<string>();

    allVehicleMarkers.forEach(v => {
      if (v.dispatchInfo?.size) sizes.add(v.dispatchInfo.size);
      if (v.dispatchInfo?.equipType) equipTypes.add(v.dispatchInfo.equipType);
      if (v.dispatchInfo?.chassisType) chassisTypes.add(v.dispatchInfo.chassisType);
    });

    return {
      sizes: Array.from(sizes).sort(),
      equipTypes: Array.from(equipTypes).sort(),
      chassisTypes: Array.from(chassisTypes).sort(),
    };
  }, [allVehicleMarkers]);

  // Select and pan to vehicle - unified function for both map clicks and list clicks
  const selectAndPanToVehicle = async (vehicleId: string) => {
    // Don't re-process if clicking the same vehicle
    if (vehicleId === selectedVehicleId) {
      return;
    }

    const vehicle = vehicleMarkers.find(v => v.id === vehicleId);
    if (vehicle && mapRef.current) {
      // Clear any existing route - the useEffect will determine if there's a new route to show
      // and handle zoom accordingly
      clearClickedRoute();
      setSelectedDispatchRoute(null);
      clearAvailableProMarkers();

      // Don't zoom here - let the useEffect handle it after currentDispatches loads
      // This way we know whether to zoom to truck or fitBounds to route

      // Don't set lastDispatchFetchRef here - let the useEffect handle it
      // This allows the useEffect to properly clear old data and show loading state

      setSelectedVehicleId(vehicleId);
      setIsInfoWindowMinimized(false); // Reset minimize state when selecting a new vehicle

      // Fetch all available pros that the driver is qualified for
      if (vehicle.dispatchInfo?.unitNumber) {
        fetchAvailablePros(vehicle.dispatchInfo.unitNumber);
      }

      // Fetch detailed dispatch info for showing dispatch started time
      // Note: currentDispatches are fetched by the useEffect at line 662
      if (vehicle.dispatchInfo?.unitNumber) {
        try {
          const response = await api.get('/driver-dispatch/latest-dispatches', {
            params: { unitnumber: vehicle.dispatchInfo.unitNumber }
          });
          const data = response.data;
          if (data.drivers && data.drivers.length > 0) {
            setDetailedDispatchInfo(data.drivers[0]);
          } else {
            setDetailedDispatchInfo(null);
          }
        } catch (error) {
          console.error('Error fetching detailed dispatch info:', error);
          setDetailedDispatchInfo(null);
        }
      } else {
        setDetailedDispatchInfo(null);
      }
    }
  };

  // Get selected vehicle for info window
  const selectedVehicle = selectedVehicleId
    ? vehicleMarkers.find(v => v.id === selectedVehicleId)
    : null;

  // Helper function to parse coordinates string (format: "lat,lng")
  const parseCoordinates = (coords: string | null): { lat: number; lng: number } | null => {
    if (!coords) return null;
    // Handle multiple coordinate pairs separated by semicolons, newlines, or both
    // Extract just the first coordinate pair
    const firstCoordPair = coords
      .split(/[\r\n;]+/)[0]  // Split by newlines or semicolons
      .trim();
    const parts = firstCoordPair.split(',').map(s => parseFloat(s.trim()));
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      // Note: coordinates might be in lng,lat format based on the data
      // Check if first value looks like longitude (typically negative in US)
      if (Math.abs(parts[0]) > 90 && Math.abs(parts[1]) < 90) {
        // Likely lng,lat format - swap them
        return { lat: parts[1], lng: parts[0] };
      }
      return { lat: parts[0], lng: parts[1] };
    }
    return null;
  };

  // Handle DriverLane move card click — draw route on map
  const handleMoveProClick = useCallback((pronumber: number, legnum: number) => {
    if (!driverMovesData) return;

    // Find all moves for this PRO number (may have multiple legs)
    const proMoves = driverMovesData.moves.filter(m => m.pronumber === pronumber);
    if (proMoves.length === 0) return;

    const isInProgress = proMoves.some(m =>
      m.status === 'In Transit' || m.status === 'At Origin' || m.status === 'At Destination'
    );
    const isCompleted = proMoves.every(m => m.status === 'Completed');

    // Don't redraw if clicking the already-selected dispatch
    if (selectedDispatchRoute && selectedDispatchRoute.proNumber === String(pronumber)) {
      return;
    }

    setSelectedDispatchRoute({
      proNumber: String(pronumber),
      isInProgress,
      isCompleted,
      legs: proMoves.map(m => ({
        origcoordinates: m.origin_lat && m.origin_lng ? `${m.origin_lat},${m.origin_lng}` : '',
        destcoordinates: m.dest_lat && m.dest_lng ? `${m.dest_lat},${m.dest_lng}` : '',
        originName: m.origin_name,
        destName: m.dest_name,
        origgeofencetype: 'CIRCULAR',
        origgeofenceradius: 0.5,
        destgeofencetype: 'CIRCULAR',
        destgeofenceradius: 0.5,
        isComplete: m.status === 'Completed'
      }))
    });
  }, [driverMovesData, selectedDispatchRoute, setSelectedDispatchRoute]);

  // Open the PRO detail drawer from a driver's move card
  const handleMoveProDetailsClick = useCallback((pronumber: number) => {
    setSelectedProNumber(pronumber);
    setIsProDetailsModalOpen(true);
  }, []);

  // Handle parking location click - pan to location and show geofence circle
  const handleParkingLocationClick = useCallback((
    coordinates: string,
    geofencetype: string,
    geofenceradius: number,
    name: string
  ) => {
    if (!mapRef.current) return;

    // Clear existing parking location circle and marker
    if (parkingLocationCircleRef.current) {
      parkingLocationCircleRef.current.setMap(null);
      parkingLocationCircleRef.current = null;
    }
    if (parkingLocationMarkerRef.current) {
      parkingLocationMarkerRef.current.map = null;
      parkingLocationMarkerRef.current = null;
    }

    const center = parseCoordinates(coordinates);
    if (!center) return;

    // Pan to the location
    mapRef.current.panTo(center);
    mapRef.current.setZoom(15);

    // Draw the geofence circle (orange/amber color for parking)
    const typeNormalized = geofencetype.toLowerCase();
    if (typeNormalized === 'circle' || typeNormalized === 'c' || typeNormalized === 'circular') {
      const radiusMeters = geofenceradius * 1609.34; // Convert miles to meters

      const circle = new google.maps.Circle({
        center: center,
        radius: radiusMeters,
        fillColor: '#F59E0B', // Amber
        fillOpacity: 0.25,
        strokeColor: '#F59E0B',
        strokeOpacity: 0.6,
        strokeWeight: 2,
        map: mapRef.current
      });

      parkingLocationCircleRef.current = circle;
    }

    // Add a marker for the parking location
    const markerContent = document.createElement('div');
    markerContent.innerHTML = `
      <div style="background: #F59E0B; color: white; padding: 4px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
        🅿️ ${name}
      </div>
    `;

    const marker = new google.maps.marker.AdvancedMarkerElement({
      map: mapRef.current,
      position: center,
      content: markerContent,
      zIndex: 1000
    });

    parkingLocationMarkerRef.current = marker;
  }, []);

  // Handle map load
  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;

    // Clean up existing clusterer and markers if map is remounting
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
      clustererRef.current.setMap(null);
      clustererRef.current = null;
    }

    // Clear old markers
    markersRef.current.forEach(marker => {
      marker.map = null;
    });
    markersRef.current = [];

    // Initialize clusterer with custom renderer using AdvancedMarkerElement
    clustererRef.current = new MarkerClusterer({
        map,
        markers: [],
        algorithm: new SuperClusterAlgorithm({
          maxZoom: 14,
          radius: 80,
        }),
        renderer: {
          render: ({ count, position }: { count: number; position: google.maps.LatLng }) => {
            // Scale cluster bubble size based on count
            const size = count < 10 ? 40 : count < 50 ? 50 : 60;
            const fontSize = count < 10 ? 18 : count < 50 ? 22 : 24;

            const clusterDiv = document.createElement('div');
            clusterDiv.style.width = `${size}px`;
            clusterDiv.style.height = `${size}px`;
            clusterDiv.innerHTML = `
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="${size}" height="${size}">
                <circle cx="50" cy="50" r="45" fill="#6B7280" stroke="#030712" strokeWidth="3" opacity="0.85"/>
                <text x="50" y="50" text-anchor="middle" dominant-baseline="central"
                      font-family="var(--app-font-family)" font-size="${fontSize}" font-weight="bold" fill="white">
                  ${count}
                </text>
              </svg>
            `;

            return new google.maps.marker.AdvancedMarkerElement({
              position,
              content: clusterDiv,
            });
          },
        },
        onClusterClick: (event: any, cluster: any, map: google.maps.Map) => {
          if (cluster.bounds) {
            map.fitBounds(cluster.bounds);
          }
        },
      });

    // Add custom map type toggle control to match Google's native controls
    // Clear any existing custom controls first (prevents duplicates if onMapLoad fires multiple times)
    const rightTopControls = map.controls[google.maps.ControlPosition.RIGHT_TOP];
    while (rightTopControls.getLength() > 0) {
      rightTopControls.pop();
    }

    const mapTypeControlDiv = document.createElement('div');
    mapTypeControlDiv.style.margin = '10px';

    const mapTypeButton = document.createElement('button');
    mapTypeButton.type = 'button';
    mapTypeButton.title = mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view';
    mapTypeButton.style.cssText = `
      background: none rgb(255, 255, 255);
      border: 0px;
      margin: 0px;
      padding: 0px;
      cursor: pointer;
      height: 40px;
      width: 40px;
      border-radius: 2px;
      box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
      overflow: hidden;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    // Set initial icon based on current mapType
    const iconStroke = isDarkMode ? '#e5e7eb' : '#555';
    const globeIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="1.5" style="width: 24px; height: 24px;"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg>`;
    const mapIcon = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="${iconStroke}" stroke-width="1.5" style="width: 24px; height: 24px;"><path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" /><path d="M9 3v15" /><path d="M15 6v15" /></svg>`;
    mapTypeButton.innerHTML = mapType === 'roadmap' ? globeIcon : mapIcon;

    mapTypeButton.addEventListener('click', () => {
      setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap');
    });

    // Store ref so we can update the icon when mapType changes
    mapTypeControlRef.current = mapTypeButton;

    mapTypeControlDiv.appendChild(mapTypeButton);
    map.controls[google.maps.ControlPosition.RIGHT_TOP].push(mapTypeControlDiv);

    // Trigger marker creation by setting mapReady to true
    // This ensures markers are created immediately when map loads
    setMapReady(true);
  }, []);

  // Handle place selection from autocomplete
  const handlePlaceSelect = useCallback((place: google.maps.places.PlaceResult) => {
    if (place.geometry?.location && mapRef.current) {
      const location = place.geometry.location;

      // Pan to the location
      mapRef.current.panTo(location);
      mapRef.current.setZoom(15);

      // Remove previous search marker if exists
      if (locationSearchMarkerRef.current) {
        locationSearchMarkerRef.current.map = null;
      }

      // Create a marker at the searched location
      const markerContent = document.createElement('div');
      markerContent.innerHTML = `
        <div style="
          width: 32px;
          height: 32px;
          background: #EF4444;
          border: 3px solid white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          box-shadow: 0 2px 6px rgba(0,0,0,0.3);
        "></div>
      `;

      const marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapRef.current,
        position: location,
        content: markerContent,
        zIndex: 2000,
      });

      locationSearchMarkerRef.current = marker;

      // Update the search query to show selected place name
      setLocationSearchQuery(place.formatted_address || place.name || '');
    }
  }, []);

  // Initialize location search autocomplete (desktop)
  useEffect(() => {
    if (!isLoaded || !locationSearchInputRef.current || locationSearchAutocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(locationSearchInputRef.current, {
      fields: ['geometry', 'name', 'formatted_address'],
      types: ['geocode', 'establishment'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      handlePlaceSelect(place);
    });

    locationSearchAutocompleteRef.current = autocomplete;
  }, [isLoaded, handlePlaceSelect]);

  // Initialize location search autocomplete (mobile)
  useEffect(() => {
    if (!isLoaded || !locationSearchMobileInputRef.current || locationSearchMobileAutocompleteRef.current) return;

    const autocomplete = new google.maps.places.Autocomplete(locationSearchMobileInputRef.current, {
      fields: ['geometry', 'name', 'formatted_address'],
      types: ['geocode', 'establishment'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      handlePlaceSelect(place);
    });

    locationSearchMobileAutocompleteRef.current = autocomplete;
  }, [isLoaded, handlePlaceSelect]);

  // Update markers when vehicleMarkers changes
  useEffect(() => {
    if (!mapReady || !mapRef.current || !clustererRef.current) return;

    // Create a map of existing markers by vehicle ID
    const existingMarkersMap = new Map<string, google.maps.marker.AdvancedMarkerElement>();
    markersRef.current.forEach((marker, index) => {
      const vehicleId = (marker as any).vehicleId;
      if (vehicleId) {
        existingMarkersMap.set(vehicleId, marker);
      }
    });

    const newMarkers: google.maps.marker.AdvancedMarkerElement[] = [];

    vehicleMarkers.forEach(vehicle => {
      const existingMarker = existingMarkersMap.get(vehicle.id);
      const newPosition = { lat: vehicle.latitude, lng: vehicle.longitude };
      const hasDispatch = !!(vehicle.dispatchInfo?.proNumber);
      const isCompleted = vehicle.dispatchInfo?.isCompleted || false;

      // Always show all markers - apply opacity to non-selected trucks when one is selected
      const shouldShow = true;
      const isThisTruckSelected = vehicle.id === selectedVehicleId;
      const isOOS = vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE';
      const isNotWorking = vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING';
      const opacity = isThisTruckSelected ? 1 : (isOOS ? 1 : (selectedVehicleId ? 0.3 : 1));

      if (existingMarker) {
        // Marker exists - update position and content directly
        existingMarker.position = newPosition;
        existingMarker.map = shouldShow ? mapRef.current : null;
        (existingMarker as any).zIndex = isThisTruckSelected ? 1000 : 1;
        // Determine truck type based on mule status, dispatch and bare chassis status
        // - Mule: Driver is a mule driver (always use mule icon)
        // - TruckEmpty: No dispatch (no PRO number) OR dispatch is completed
        // - TruckWithChassis: Has dispatch and is on bare chassis move
        // - TruckWithContainer: Has dispatch and has container
        const vehicleIsMule = vehicle.dispatchInfo?.isMule || false;
        const truckType: TruckType = vehicleIsMule
          ? 'Mule'
          : (!hasDispatch || isCompleted
            ? 'TruckEmpty'
            : (vehicle.dispatchInfo?.isBareChassis ? 'TruckWithChassis' : 'TruckWithContainer'));
        const hasOriginArrivalTime = !!vehicle.dispatchInfo?.originArrivalDateTime;

        // Determine base size for icon (needed for easter egg too)
        const baseSize = truckType === 'TruckEmpty' ? 32 : truckType === 'Mule' ? 32 : 60;
        const size = Math.round(baseSize * iconSizeMultiplier);

        // If mule easter egg already triggered, keep showing donkey
        const isMuleEasterEgg = muleEasterEggVehiclesRef.current.has(vehicle.id);
        const markerContent = isMuleEasterEgg
          ? createMuleEasterEggIcon(
              isOOS ? `rgba(239,68,68,${opacity})` : isNotWorking ? `rgba(255,250,160,${opacity})` :
              (hasDispatch && !isCompleted) ? `rgba(59,130,246,${opacity})` :
              ((vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !hasOriginArrivalTime) ? `rgba(245,158,11,${opacity})` :
              (hasDispatch && isCompleted && (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0) ? `rgba(107,114,128,${opacity})` :
              `rgba(16,185,129,${opacity})`,
              size
            )
          : createVehicleIcon(
              vehicle.speedMilesPerHour || 0,
              vehicle.headingDegrees,
              hasDispatch,
              isCompleted,
              vehicle.dispatchInfo?.remainingDispatchesToday || 0,
              hasOriginArrivalTime,
              truckType,
              iconSizeMultiplier,
              vehicle.dispatchInfo?.unitNumber || undefined,
              opacity,
              isOOS,
              isNotWorking,
              isDarkMode
            );

        // Add hover event listeners for tooltip
        const vehicleInfo = {
          unitNumber: vehicle.dispatchInfo?.unitNumber || undefined,
          driverName: vehicle.dispatchInfo?.driverName || undefined,
          proNumber: vehicle.dispatchInfo?.proNumber || undefined,
          completedToday: isCompleted ? 1 : 0,
          remainingDispatches: vehicle.dispatchInfo?.remainingDispatchesToday || 0,
          isOOS,
          isNotWorking,
          // Endorsements
          has93k: vehicle.dispatchInfo?.has93k || false,
          hasHazmat: vehicle.dispatchInfo?.hasHazmat || false,
          hasTank: vehicle.dispatchInfo?.hasTank || false,
          isLightTruck: vehicle.dispatchInfo?.isLightTruck || false,
          isDaycab: vehicle.dispatchInfo?.isDaycab || false,
          isMule: vehicle.dispatchInfo?.isMule || false
        };
        markerContent.addEventListener('mouseenter', (e) => {
          const rect = markerContent.getBoundingClientRect();
          setHoveredVehicleRef.current({
            id: vehicle.id,
            position: { x: rect.left + rect.width / 2, y: rect.top },
            info: vehicleInfo
          });
          // Mule easter egg: start 30s hover timer
          if (vehicleIsMule && !muleEasterEggVehiclesRef.current.has(vehicle.id)) {
            if (muleHoverTimerRef.current) clearTimeout(muleHoverTimerRef.current);
            muleHoverTimerRef.current = setTimeout(() => {
              muleEasterEggVehiclesRef.current.add(vehicle.id);
              // Replace marker with donkey SVG
              const donkeyColor = isOOS ? `rgba(239,68,68,${opacity})` : isNotWorking ? `rgba(255,250,160,${opacity})` :
                (hasDispatch && !isCompleted) ? `rgba(59,130,246,${opacity})` :
                ((vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !hasOriginArrivalTime) ? `rgba(245,158,11,${opacity})` :
                (hasDispatch && isCompleted && (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0) ? `rgba(107,114,128,${opacity})` :
                `rgba(16,185,129,${opacity})`;
              const donkeyContainer = createMuleEasterEggIcon(donkeyColor, size);
              existingMarker.content = donkeyContainer;
              // Copy over event listeners for the new content
              donkeyContainer.addEventListener('mouseenter', (e2) => {
                const r = donkeyContainer.getBoundingClientRect();
                setHoveredVehicleRef.current({ id: vehicle.id, position: { x: r.left + r.width / 2, y: r.top }, info: vehicleInfo });
              });
              donkeyContainer.addEventListener('mouseleave', () => { setHoveredVehicleRef.current(null); });
              donkeyContainer.addEventListener('click', () => { selectAndPanToVehicle(vehicle.id); });
              // Record achievement
              const userEmail = accounts[0]?.username || 'unknown@example.com';
              api.post('/achievements/record', { achievement_id: 'found_a_mule', email: userEmail })
                .then(response => {
                  if (response.data.first_discovery) {
                    setAchievementMessage({ title: 'Found A Mule', id: 'found_a_mule' });
                    setShowAchievementNotification(true);
                    celebrateAchievement();
                  }
                })
                .catch(error => { console.error('Failed to record achievement:', error); });
            }, 30000);
          }
        });
        markerContent.addEventListener('mouseleave', () => {
          setHoveredVehicleRef.current(null);
          // Clear mule easter egg timer
          if (muleHoverTimerRef.current) {
            clearTimeout(muleHoverTimerRef.current);
            muleHoverTimerRef.current = null;
          }
        });

        existingMarker.content = markerContent;

        newMarkers.push(existingMarker);
        existingMarkersMap.delete(vehicle.id);
      } else {
        // New marker - create it
        // Determine truck type based on mule status, dispatch and bare chassis status
        // - Mule: Driver is a mule driver (always use mule icon)
        // - TruckEmpty: No dispatch (no PRO number) OR dispatch is completed
        // - TruckWithChassis: Has dispatch and is on bare chassis move
        // - TruckWithContainer: Has dispatch and has container
        const vehicleIsMule2 = vehicle.dispatchInfo?.isMule || false;
        const truckType: TruckType = vehicleIsMule2
          ? 'Mule'
          : (!hasDispatch || isCompleted
            ? 'TruckEmpty'
            : (vehicle.dispatchInfo?.isBareChassis ? 'TruckWithChassis' : 'TruckWithContainer'));
        const hasOriginArrivalTime = !!vehicle.dispatchInfo?.originArrivalDateTime;
        const muleBaseSize = truckType === 'TruckEmpty' ? 32 : truckType === 'Mule' ? 32 : 60;
        const muleIconSize = Math.round(muleBaseSize * iconSizeMultiplier);
        const markerContent = createVehicleIcon(
          vehicle.speedMilesPerHour || 0,
          vehicle.headingDegrees,
          hasDispatch,
          isCompleted,
          vehicle.dispatchInfo?.remainingDispatchesToday || 0,
          hasOriginArrivalTime,
          truckType,
          iconSizeMultiplier,
          vehicle.dispatchInfo?.unitNumber || undefined,
          opacity,
          isOOS,
          isNotWorking,
          isDarkMode
        );

        // Add hover event listeners for tooltip
        const vehicleInfo = {
          unitNumber: vehicle.dispatchInfo?.unitNumber || undefined,
          driverName: vehicle.dispatchInfo?.driverName || undefined,
          proNumber: vehicle.dispatchInfo?.proNumber || undefined,
          completedToday: isCompleted ? 1 : 0,
          remainingDispatches: vehicle.dispatchInfo?.remainingDispatchesToday || 0,
          isOOS,
          isNotWorking,
          // Endorsements
          has93k: vehicle.dispatchInfo?.has93k || false,
          hasHazmat: vehicle.dispatchInfo?.hasHazmat || false,
          hasTank: vehicle.dispatchInfo?.hasTank || false,
          isLightTruck: vehicle.dispatchInfo?.isLightTruck || false,
          isDaycab: vehicle.dispatchInfo?.isDaycab || false,
          isMule: vehicle.dispatchInfo?.isMule || false
        };
        markerContent.addEventListener('mouseenter', (e) => {
          const rect = markerContent.getBoundingClientRect();
          setHoveredVehicleRef.current({
            id: vehicle.id,
            position: { x: rect.left + rect.width / 2, y: rect.top },
            info: vehicleInfo
          });
          // Mule easter egg: start 30s hover timer
          if (vehicleIsMule2 && !muleEasterEggVehiclesRef.current.has(vehicle.id)) {
            if (muleHoverTimerRef.current) clearTimeout(muleHoverTimerRef.current);
            muleHoverTimerRef.current = setTimeout(() => {
              muleEasterEggVehiclesRef.current.add(vehicle.id);
              const donkeyColor = isOOS ? `rgba(239,68,68,${opacity})` : isNotWorking ? `rgba(255,250,160,${opacity})` :
                (hasDispatch && !isCompleted) ? `rgba(59,130,246,${opacity})` :
                ((vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !hasOriginArrivalTime) ? `rgba(245,158,11,${opacity})` :
                (hasDispatch && isCompleted && (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0) ? `rgba(107,114,128,${opacity})` :
                `rgba(16,185,129,${opacity})`;
              const donkeyContainer = createMuleEasterEggIcon(donkeyColor, muleIconSize);
              marker.content = donkeyContainer;
              donkeyContainer.addEventListener('mouseenter', (e2) => {
                const r = donkeyContainer.getBoundingClientRect();
                setHoveredVehicleRef.current({ id: vehicle.id, position: { x: r.left + r.width / 2, y: r.top }, info: vehicleInfo });
              });
              donkeyContainer.addEventListener('mouseleave', () => { setHoveredVehicleRef.current(null); });
              donkeyContainer.addEventListener('click', () => { selectAndPanToVehicle(vehicle.id); });
              const userEmail = accounts[0]?.username || 'unknown@example.com';
              api.post('/achievements/record', { achievement_id: 'found_a_mule', email: userEmail })
                .then(response => {
                  if (response.data.first_discovery) {
                    setAchievementMessage({ title: 'Found A Mule', id: 'found_a_mule' });
                    setShowAchievementNotification(true);
                    celebrateAchievement();
                  }
                })
                .catch(error => { console.error('Failed to record achievement:', error); });
            }, 30000);
          }
        });
        markerContent.addEventListener('mouseleave', () => {
          setHoveredVehicleRef.current(null);
          if (muleHoverTimerRef.current) {
            clearTimeout(muleHoverTimerRef.current);
            muleHoverTimerRef.current = null;
          }
        });

        const marker = new google.maps.marker.AdvancedMarkerElement({
          position: newPosition,
          content: markerContent,
          map: shouldShow ? null : null,
          zIndex: isThisTruckSelected ? 1000 : 1,
        }) as any;

        marker.vehicleId = vehicle.id;

        marker.addListener('click', () => {
          selectAndPanToVehicle(vehicle.id);
        });

        newMarkers.push(marker);
      }
    });

    // Remove markers that no longer exist
    existingMarkersMap.forEach(marker => {
      marker.map = null;
    });

    markersRef.current = newMarkers;
    clustererRef.current.clearMarkers();

    // Add markers to clusterer - exclude selected marker so it's always visible
    const markersForClusterer = newMarkers.filter(marker => {
      const vehicleId = (marker as any).vehicleId;
      return vehicleId !== selectedVehicleId;
    });

    // Ensure selected marker is rendered directly on the map (not in clusterer)
    newMarkers.forEach(marker => {
      const vehicleId = (marker as any).vehicleId;
      if (vehicleId === selectedVehicleId) {
        marker.map = mapRef.current;
      }
    });

    clustererRef.current.addMarkers(markersForClusterer);
  }, [mapReady, vehicleMarkers, selectedVehicleId, iconSizeMultiplier, isDarkMode]);

  // Helper to clear clicked dispatch route elements
  const clearClickedRoute = useCallback(() => {
    if (clickedDispatchRouteRendererRef.current) {
      clickedDispatchRouteRendererRef.current.setMap(null);
      clickedDispatchRouteRendererRef.current = null;
    }
    if (clickedDispatchTraveledRouteRendererRef.current) {
      clickedDispatchTraveledRouteRendererRef.current.setMap(null);
      clickedDispatchTraveledRouteRendererRef.current = null;
    }
    if (clickedDispatchOriginMarkerRef.current) {
      clickedDispatchOriginMarkerRef.current.map = null;
      clickedDispatchOriginMarkerRef.current = null;
    }
    if (clickedDispatchDestMarkerRef.current) {
      clickedDispatchDestMarkerRef.current.map = null;
      clickedDispatchDestMarkerRef.current = null;
    }
    // Clear intermediate markers
    clickedDispatchIntermediateMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    clickedDispatchIntermediateMarkersRef.current = [];
    // Clear geofences
    clickedDispatchGeofencesRef.current.forEach(geofence => {
      geofence.setMap(null);
    });
    clickedDispatchGeofencesRef.current = [];
  }, []);

  // Helper to clear available pro markers
  const clearAvailableProMarkers = useCallback(() => {
    availableProMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    availableProMarkersRef.current = [];
    setAvailablePros([]);
  }, []);

  // Fetch available pros that the driver is qualified for
  const fetchAvailablePros = useCallback(async (unitNumber: string) => {
    try {
      const response = await api.get('/orders/available-pros', {
        params: { unit_number: unitNumber }
      });
      const data = response.data;

      if (data.data && Array.isArray(data.data)) {
        setAvailablePros(data.data);
      } else {
        setAvailablePros([]);
      }
    } catch (error) {
      console.error('Error fetching available pros:', error);
      setAvailablePros([]);
    }
  }, []);


  // Store the current unit number in a ref for polling
  const currentPollingUnitRef = useRef<string | null>(null);

  // Keep currentPollingUnitRef in sync with selected vehicle
  useEffect(() => {
    if (!selectedVehicleId) {
      currentPollingUnitRef.current = null;
      return;
    }
    const selectedVehicle = vehicleMarkers.find(v => v.id === selectedVehicleId);
    currentPollingUnitRef.current = selectedVehicle?.dispatchInfo?.unitNumber ?? null;
  }, [selectedVehicleId, vehicleMarkers]);

  // Poll available pros every 30 seconds when a driver is selected
  useVisibilityAwareInterval(
    () => {
      if (currentPollingUnitRef.current) {
        fetchAvailablePros(currentPollingUnitRef.current);
      }
    },
    selectedVehicleId ? 30000 : null
  );

  // Socket.IO listener for real-time available moves updates
  // When another dispatcher assigns a driver, this triggers an immediate refresh
  const socketRef = useRef<ReturnType<typeof io> | null>(null);

  // Keep a ref to vehicleMarkers for socket initialization lookup
  // This avoids re-running the socket effect when vehicle positions update
  const vehicleMarkersRef = useRef(vehicleMarkers);
  useEffect(() => {
    vehicleMarkersRef.current = vehicleMarkers;
  }, [vehicleMarkers]);

  useEffect(() => {
    // Only establish socket connection when a driver is selected
    if (!selectedVehicleId) {
      return;
    }

    const selectedVehicle = vehicleMarkersRef.current.find(v => v.id === selectedVehicleId);
    if (!selectedVehicle?.dispatchInfo?.unitNumber) {
      return;
    }

    const unitNumber = selectedVehicle.dispatchInfo.unitNumber;

    // Track if the effect has been cleaned up to prevent race conditions
    let isCancelled = false;

    const initializeSocket = async () => {
      try {
        const accounts = msalInstance.getAllAccounts();
        if (!accounts || accounts.length === 0) {
          return;
        }

        const request = {
          ...apiTokenRequest,
          account: accounts[0],
        };
        const response = await msalInstance.acquireTokenSilent(request);

        // Check if component unmounted during token acquisition
        if (isCancelled) {
          return;
        }

        if (!response.accessToken) {
          return;
        }

        const socketUrl = config.API_URL.replace('/api/v1', '');

        const socket = io(socketUrl, {
          path: '/ws',
          auth: {
            token: `Bearer ${response.accessToken}`
          },
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionDelay: 2000,
          reconnectionDelayMax: 10000,
          reconnectionAttempts: 3,
          timeout: 10000,
        });

        // Check again after socket creation - if cancelled, disconnect immediately
        if (isCancelled) {
          socket.disconnect();
          return;
        }

        socketRef.current = socket;

        // Listen for available moves updates
        socket.on('available_moves_update', (data: { update_type: string; pronumber: number; legnum: number }) => {
          console.log('[Socket.IO] Available moves update received:', data);
          // Refresh available pros for the currently selected driver
          fetchAvailablePros(unitNumber);
        });

        socket.on('connect_error', (error: Error) => {
          console.warn('[Socket.IO LiveMap] Connection error:', error.message);
        });
      } catch (error) {
        console.warn('[Socket.IO LiveMap] Failed to initialize socket:', error);
      }
    };

    initializeSocket();

    return () => {
      // Mark as cancelled to prevent async operations from continuing
      isCancelled = true;
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [selectedVehicleId, fetchAvailablePros]);

  // Store current selectedProClusterList in a ref to avoid dependency loop
  const selectedProClusterListRef = useRef<any[][] | null>(null);

  // Keep ref in sync with state
  useEffect(() => {
    selectedProClusterListRef.current = selectedProClusterList;
  }, [selectedProClusterList]);

  // Update selectedProClusterList when availablePros updates (from polling)
  useEffect(() => {
    if (!selectedProClusterListRef.current || selectedProClusterListRef.current.length === 0) {
      return;
    }

    // Get all pro numbers from all clusters in the list
    const allProNumbers = selectedProClusterListRef.current.flat().map((p: any) => p.pronumber);

    // Update with fresh data from availablePros
    const updatedPros = availablePros.filter(p => allProNumbers.includes(p.pronumber));

    if (updatedPros.length > 0) {
      // Maintain the cluster structure - wrap in array to keep as list of clusters
      setSelectedProClusterList([updatedPros]);
    }
  }, [availablePros]);

  // Create container icon for pro markers
  const createContainerIcon = (proNumber: string) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.cursor = 'pointer';

    container.innerHTML = `
      <div style="background: #F3E8FF; border: 2px solid #A855F7; border-radius: 4px; padding: 4px 8px; font-size: 12px; font-weight: bold; white-space: nowrap; color: #7E22CE; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
        ${proNumber}
      </div>
    `;

    return container;
  };

  // Create cluster icon for multiple pros
  const createClusterIcon = (count: number) => {
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.transform = 'translate(-50%, -50%)';
    container.style.cursor = 'pointer';

    container.innerHTML = `
      <div style="background: #F3E8FF; border: 3px solid #A855F7; border-radius: 50%; width: 40px; height: 40px; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: bold; color: #7E22CE; box-shadow: 0 2px 6px rgba(0,0,0,0.3);">
        ${count}
      </div>
    `;

    return container;
  };

  // Group pros by location (within ~100 meters)
  const clusterPros = useCallback((pros: any[]) => {
    const clusters: { [key: string]: any[] } = {};
    const CLUSTER_DISTANCE = 0.001; // roughly 100 meters

    pros.forEach(pro => {
      const coords = parseCoordinates(pro.originCoordinates);
      if (!coords) return;

      // Round coordinates to group nearby pros
      const clusterKey = `${Math.round(coords.lat / CLUSTER_DISTANCE)}_${Math.round(coords.lng / CLUSTER_DISTANCE)}`;

      if (!clusters[clusterKey]) {
        clusters[clusterKey] = [];
      }
      clusters[clusterKey].push({ ...pro, coords });
    });

    return Object.values(clusters);
  }, [parseCoordinates]);

  // Auto-select in-progress dispatch when currentDispatches loads
  useEffect(() => {
    if (!currentDispatches || !selectedVehicle || !selectedVehicle.dispatchInfo) {
      clearClickedRoute();
      setSelectedDispatchRoute(null); // Clear any clicked dispatch route

      // Don't zoom here - we might be waiting for currentDispatches to load
      // Only zoom after we know for sure there's no route (see else blocks below)
      return;
    }

    const dispatch = selectedVehicle.dispatchInfo;

    // Check if vehicle has an in-progress dispatch (not completed)
    const hasInProgressDispatch = dispatch.proNumber && !dispatch.isCompleted;

    if (hasInProgressDispatch && currentDispatches.dispatches && currentDispatches.dispatches.length > 0) {
      // Find all legs for the in-progress PRO number
      const inProgressLegs = currentDispatches.dispatches.filter(
        (d: any) => String(d.pronumber) === String(dispatch.proNumber)
      );

      if (inProgressLegs.length > 0) {
        // Auto-set selectedDispatchRoute to show the in-progress route with all stops
        // The route drawing effect will handle fitBounds
        setSelectedDispatchRoute({
          proNumber: String(dispatch.proNumber),
          isInProgress: true,
          isCompleted: false,
          legs: inProgressLegs.map((leg: any) => ({
            origcoordinates: leg.origcoordinates,
            destcoordinates: leg.destcoordinates,
            originName: leg.originName,
            destName: leg.destName,
            origgeofencetype: leg.origgeofencetype,
            origgeofenceradius: leg.origgeofenceradius,
            destgeofencetype: leg.destgeofencetype,
            destgeofenceradius: leg.destgeofenceradius,
            isComplete: leg.isComplete || false
          }))
        });
      } else {
        // Has dispatch info but no route legs - zoom to truck
        clearClickedRoute();
        setSelectedDispatchRoute(null);
        if (mapRef.current) {
          mapRef.current.panTo({ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude });
          mapRef.current.setZoom(16);
        }
      }
    } else {
      // No in-progress dispatch - zoom to truck
      clearClickedRoute();
      setSelectedDispatchRoute(null);
      if (mapRef.current) {
        mapRef.current.panTo({ lat: selectedVehicle.latitude, lng: selectedVehicle.longitude });
        mapRef.current.setZoom(16);
      }
    }
  }, [currentDispatches, selectedVehicleId, clearClickedRoute]);

  // Draw route for clicked dispatch (in-progress, not-started, or completed dispatches)
  useEffect(() => {
    // Helper to draw geofence on map
    const drawGeofence = (
      coordinates: string,
      geofenceType: string,
      radiusMiles: number | string,
      map: google.maps.Map
    ): google.maps.Circle | google.maps.Polygon | null => {
      if (!coordinates || !geofenceType) {
        return null;
      }

      // Choose color based on dispatch status: blue for in-progress, green for completed, purple for not-started
      const fillColor = selectedDispatchRoute?.isInProgress ? '#3B82F6' : selectedDispatchRoute?.isCompleted ? '#10B981' : '#9333EA';
      const strokeColor = selectedDispatchRoute?.isInProgress ? '#3B82F6' : selectedDispatchRoute?.isCompleted ? '#10B981' : '#9333EA';
      const fillOpacity = 0.25; // 25% opacity
      const strokeOpacity = 0.5; // 50% opacity
      const strokeWeight = 2;

      const typeNormalized = geofenceType.toLowerCase();

      if (typeNormalized === 'circle' || typeNormalized === 'c' || typeNormalized === 'circular') {
        const center = parseCoordinates(coordinates);
        if (!center) {
          // Silently skip if center coordinates cannot be parsed
          return null;
        }

        // Convert radiusMiles to number if it's a string, then convert miles to meters (1 mile = 1609.34 meters)
        const radiusInMiles = typeof radiusMiles === 'string' ? parseFloat(radiusMiles) : radiusMiles;
        const radiusMeters = radiusInMiles * 1609.34;

        const circle = new google.maps.Circle({
          center: center,
          radius: radiusMeters,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          strokeColor: strokeColor,
          strokeOpacity: strokeOpacity,
          strokeWeight: strokeWeight,
          map: map
        });

        return circle;
      } else if (geofenceType.toLowerCase() === 'polygon' || geofenceType.toLowerCase() === 'p') {
        // Parse semicolon-separated coordinates
        const coordPairs = coordinates.split(';').map(pair => pair.trim()).filter(pair => pair);
        const polygonPath: google.maps.LatLngLiteral[] = [];

        for (const pair of coordPairs) {
          const parsed = parseCoordinates(pair);
          if (parsed) {
            polygonPath.push(parsed);
          }
        }

        if (polygonPath.length < 3) return null; // Need at least 3 points for a polygon

        const polygon = new google.maps.Polygon({
          paths: polygonPath,
          fillColor: fillColor,
          fillOpacity: fillOpacity,
          strokeColor: strokeColor,
          strokeOpacity: strokeOpacity,
          strokeWeight: strokeWeight,
          map: map
        });

        return polygon;
      }

      return null;
    };

    // Clear old route before drawing new one
    clearClickedRoute();

    if (!mapRef.current || !selectedDispatchRoute || !selectedDispatchRoute.legs.length) {
      return;
    }

    // Build the complete route: origin of first leg → dest of first leg → origin of second leg → dest of second leg...
    // For Google Maps Directions API, we need: origin, destination, and waypoints
    const allPoints: Array<{ coords: string; name: string; isOrigin: boolean; isDestination: boolean }> = [];

    selectedDispatchRoute.legs.forEach((leg, index) => {
      // Add origin of this leg
      allPoints.push({
        coords: leg.origcoordinates,
        name: leg.originName,
        isOrigin: index === 0,
        isDestination: false
      });

      // Add destination of this leg
      allPoints.push({
        coords: leg.destcoordinates,
        name: leg.destName,
        isOrigin: false,
        isDestination: index === selectedDispatchRoute.legs.length - 1
      });
    });

    // Parse all coordinates
    const parsedPoints = allPoints.map(point => ({
      ...point,
      position: parseCoordinates(point.coords)
    })).filter(point => point.position !== null);

    if (parsedPoints.length === 0) {
      // No valid coordinates at all
      clearClickedRoute();
      return;
    }

    if (parsedPoints.length === 1) {
      // Only one valid coordinate - show just the pin without a route
      const point = parsedPoints[0];
      let pin;

      if (point.isOrigin) {
        const originColor = selectedDispatchRoute.isInProgress ? '#10B981' : '#9333EA';
        pin = new google.maps.marker.PinElement({
          background: originColor,
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.0,
        });
        clickedDispatchOriginMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: point.position!,
          map: mapRef.current,
          content: pin.element,
          title: `Origin: ${point.name}`,
        });
      } else if (point.isDestination) {
        pin = new google.maps.marker.PinElement({
          background: '#EF4444',
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.0,
        });
        clickedDispatchDestMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: point.position!,
          map: mapRef.current,
          content: pin.element,
          title: `Destination: ${point.name}`,
        });
      }

      // Pan to the single point
      mapRef.current?.panTo(point.position!);
      if (!selectedDispatchRoute?.skipZoom) {
        mapRef.current?.setZoom(14);
      }
      return;
    }

    // Create markers for all stops
    parsedPoints.forEach((point, index) => {
      let pin;

      if (point.isOrigin) {
        // Green pin for in-progress origin, Purple for not-started/completed
        const originColor = selectedDispatchRoute.isInProgress ? '#10B981' : '#9333EA';
        pin = new google.maps.marker.PinElement({
          background: originColor,
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.0,
        });
        clickedDispatchOriginMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: point.position!,
          map: mapRef.current,
          content: pin.element,
          title: `Origin: ${point.name}`,
        });
      } else if (point.isDestination) {
        // Red pin for in-progress destination, Orange for not-started/completed
        const destColor = selectedDispatchRoute.isInProgress ? '#EF4444' : '#F97316';
        pin = new google.maps.marker.PinElement({
          background: destColor,
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 1.0,
        });
        clickedDispatchDestMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          position: point.position!,
          map: mapRef.current,
          content: pin.element,
          title: `Destination: ${point.name}`,
        });
      } else {
        // Yellow pin for intermediate stops
        pin = new google.maps.marker.PinElement({
          background: '#EAB308',
          borderColor: '#ffffff',
          glyphColor: '#ffffff',
          scale: 0.8,
        });
        const intermediateMarker = new google.maps.marker.AdvancedMarkerElement({
          position: point.position!,
          map: mapRef.current,
          content: pin.element,
          title: point.name,
        });
        clickedDispatchIntermediateMarkersRef.current.push(intermediateMarker);
      }
    });

    // Draw geofences for ALL origins and destinations
    // Track already-drawn coordinates to avoid duplicates (e.g., when dest of leg N == origin of leg N+1)
    const drawnGeofenceCoords = new Set<string>();

    selectedDispatchRoute.legs.forEach((leg) => {
      // Draw origin geofence
      if (leg.origgeofencetype && leg.origgeofenceradius && !drawnGeofenceCoords.has(leg.origcoordinates)) {
        const originGeofence = drawGeofence(
          leg.origcoordinates,
          leg.origgeofencetype,
          leg.origgeofenceradius,
          mapRef.current!
        );
        if (originGeofence) {
          clickedDispatchGeofencesRef.current.push(originGeofence);
          drawnGeofenceCoords.add(leg.origcoordinates);
        }
      }

      // Draw destination geofence
      if (leg.destgeofencetype && leg.destgeofenceradius && !drawnGeofenceCoords.has(leg.destcoordinates)) {
        const destGeofence = drawGeofence(
          leg.destcoordinates,
          leg.destgeofencetype,
          leg.destgeofenceradius,
          mapRef.current!
        );
        if (destGeofence) {
          clickedDispatchGeofencesRef.current.push(destGeofence);
          drawnGeofenceCoords.add(leg.destcoordinates);
        }
      }
    });

    // For in-progress dispatches, draw traveled route
    // This includes all completed legs + partial current leg (origin to driver position)
    if (selectedDispatchRoute.isInProgress && selectedVehicle) {
      const driverPosition = { lat: selectedVehicle.latitude, lng: selectedVehicle.longitude };

      // Validate driver position
      if (driverPosition.lat >= -90 && driverPosition.lat <= 90 &&
          driverPosition.lng >= -180 && driverPosition.lng <= 180 &&
          parsedPoints.length > 0) {

        // Build waypoints for traveled route:
        // - Include destinations of COMPLETED legs only
        // - End at driver's current position
        const traveledWaypoints: google.maps.DirectionsWaypoint[] = [];

        selectedDispatchRoute.legs.forEach((leg, index) => {
          // Only add destinations of completed legs as waypoints
          // Don't add the last leg's destination (that's the final destination, not a waypoint)
          if (leg.isComplete && index < selectedDispatchRoute.legs.length - 1) {
            const destCoords = parseCoordinates(leg.destcoordinates);
            if (destCoords) {
              traveledWaypoints.push({
                location: destCoords,
                stopover: true
              });
            }
          }
        });

        // Request traveled route from first origin through all intermediate stops to driver position
        const traveledDirectionsService = new google.maps.DirectionsService();

        traveledDirectionsService.route(
          {
            origin: parsedPoints[0].position!,
            destination: driverPosition,
            waypoints: traveledWaypoints,
            travelMode: google.maps.TravelMode.DRIVING,
          },
          (traveledResult, traveledStatus) => {
            if (traveledStatus === google.maps.DirectionsStatus.OK && traveledResult) {
              // Create renderer for traveled route with dashed light blue line
              if (!clickedDispatchTraveledRouteRendererRef.current) {
                clickedDispatchTraveledRouteRendererRef.current = new google.maps.DirectionsRenderer({
                  map: mapRef.current,
                  suppressMarkers: true,
                  preserveViewport: true,
                  polylineOptions: {
                    strokeColor: '#93C5FD', // Light blue (Tailwind blue-300)
                    strokeWeight: 3,
                    strokeOpacity: 0.6,
                    icons: [{
                      icon: {
                        path: 'M 0,-1 0,1',
                        strokeOpacity: 1,
                        scale: 3
                      },
                      offset: '0',
                      repeat: '15px'
                    }]
                  },
                });
              }
              clickedDispatchTraveledRouteRendererRef.current.setDirections(traveledResult);
            }
          }
        );
      }
    }

    // Build waypoints and origin based on dispatch status
    let waypoints: google.maps.DirectionsWaypoint[] = [];
    let routeOrigin: google.maps.LatLngLiteral;

    if (selectedDispatchRoute.isInProgress && selectedVehicle) {
      // For in-progress, route starts from driver's current position
      routeOrigin = { lat: selectedVehicle.latitude, lng: selectedVehicle.longitude };

      // Include waypoints for all remaining stops (not yet completed)
      // The solid blue line should show the planned route through all future stops
      selectedDispatchRoute.legs.forEach((leg, index) => {
        // Add destination as waypoint if this leg is not complete and not the last leg
        if (!leg.isComplete && index < selectedDispatchRoute.legs.length - 1) {
          const destCoords = parseCoordinates(leg.destcoordinates);
          if (destCoords) {
            waypoints.push({
              location: destCoords,
              stopover: true
            });
          }
        }
      });
    } else {
      // For not-started/completed dispatches, use all intermediate points as waypoints
      routeOrigin = parsedPoints[0].position!;
      waypoints = parsedPoints.slice(1, -1).map(point => ({
        location: point.position!,
        stopover: true
      }));
    }

    // Calculate route with waypoints
    const directionsService = new google.maps.DirectionsService();

    directionsService.route(
      {
        origin: routeOrigin,
        destination: parsedPoints[parsedPoints.length - 1].position!,
        waypoints: waypoints,
        travelMode: google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === google.maps.DirectionsStatus.OK && result) {
          // Choose color based on dispatch status: blue for in-progress, green for completed, purple for not-started
          const routeColor = selectedDispatchRoute?.isInProgress ? '#3B82F6' : selectedDispatchRoute?.isCompleted ? '#10B981' : '#9333EA';

          // Create directions renderer with conditional color
          if (!clickedDispatchRouteRendererRef.current) {
            clickedDispatchRouteRendererRef.current = new google.maps.DirectionsRenderer({
              map: mapRef.current,
              suppressMarkers: true, // We're using custom markers
              preserveViewport: true,
              polylineOptions: {
                strokeColor: routeColor,
                strokeWeight: 4,
                strokeOpacity: 0.7,
              },
            });
          } else {
            // Update existing renderer's polyline options
            clickedDispatchRouteRendererRef.current.setOptions({
              polylineOptions: {
                strokeColor: routeColor,
                strokeWeight: 4,
                strokeOpacity: 0.7,
              },
            });
          }
          clickedDispatchRouteRendererRef.current.setDirections(result);

          // Calculate and set route info (distance, duration, ETA) for in-progress dispatches
          // Use haversine distance to save API costs instead of Google Maps duration
          if (result.routes[0]?.legs[0] && selectedDispatchRoute?.isInProgress && selectedVehicle) {
            const finalDest = parsedPoints[parsedPoints.length - 1].position;
            if (finalDest) {
              // Calculate haversine distance from driver's current position to final destination
              const distanceMiles = haversineDistance(
                selectedVehicle.latitude,
                selectedVehicle.longitude,
                finalDest.lat,
                finalDest.lng
              );

              // Calculate duration using driver's current speed with 45 mph floor
              // Floor handles stops at lights, highway breaks, etc.
              const effectiveSpeed = Math.max(selectedVehicle.speedMilesPerHour || AVERAGE_TRUCK_SPEED_MPH, AVERAGE_TRUCK_SPEED_MPH);
              const durationMinutes = (distanceMiles / effectiveSpeed) * 60 * 1.15;

              // Format distance and duration
              const distance = distanceMiles < 1
                ? `${(distanceMiles * 5280).toFixed(0)} ft`
                : `${distanceMiles.toFixed(1)} mi`;
              const duration = formatDuration(durationMinutes);

              // Calculate ETA
              const etaDate = new Date(Date.now() + durationMinutes * 60 * 1000);
              const etaString = etaDate.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
              });

              // Set routeInfo with distance, duration, and ETA
              setRouteInfo({ distance, duration, eta: etaString });
            }
          }

          // Fit map to show entire route (unless skipZoom is set)
          if (result.routes[0] && result.routes[0].bounds) {
            if (selectedDispatchRoute?.skipZoom) {
              // Just pan to center of route without zooming
              const bounds = result.routes[0].bounds;
              const center = bounds.getCenter();
              mapRef.current?.panTo(center);
            } else {
              // Zoom to fit entire route
              mapRef.current?.fitBounds(result.routes[0].bounds);
            }
          }
        } else {
          console.warn('Clicked dispatch route request failed:', status);
        }
      }
    );
  }, [selectedDispatchRoute, selectedVehicleId]);

  // Render available pro markers on the map
  useEffect(() => {
    if (!mapRef.current || !mapReady) {
      return;
    }

    // Clear existing pro markers
    availableProMarkersRef.current.forEach(marker => {
      marker.map = null;
    });
    availableProMarkersRef.current = [];

    // Show available pros for selected driver
    const prosToShow = availablePros;

    // Create new pro markers with clustering
    if (prosToShow.length > 0) {
      const clusters = clusterPros(prosToShow);

      // Store clusters in ref for viewport calculations
      availableProClustersRef.current = clusters;

      // Calculate visible clusters immediately
      const bounds = mapRef.current.getBounds();
      if (bounds) {
        const visible = clusters.filter(cluster => {
          if (cluster.length === 0) return false;
          const coords = cluster[0].coords;
          return coords && bounds.contains(coords);
        });

        // Only update state if the visible clusters actually changed
        // Compare by creating a string key of pro numbers
        const visibleProNumbers = visible.flat().map(p => p.pronumber).sort().join(',');

        if (visibleProNumbers !== lastVisibleProNumbersRef.current) {
          lastVisibleProNumbersRef.current = visibleProNumbers;
          setVisibleProClusters(visible);
        }
      }

      const newProMarkers = clusters.map((cluster, index) => {
        if (cluster.length === 0) return null;

        // Get the center point of the cluster (use first pro's coordinates)
        const centerCoords = cluster[0].coords;
        if (!centerCoords) return null;

        let iconElement: HTMLElement;
        let marker: google.maps.marker.AdvancedMarkerElement;

        if (cluster.length === 1) {
          // Single pro - show pro number
          const pro = cluster[0];

          iconElement = createContainerIcon(pro.pronumber);

          marker = new google.maps.marker.AdvancedMarkerElement({
            position: centerCoords,
            map: mapRef.current,
            content: iconElement,
            title: `PRO: ${pro.pronumber} - ${pro.originName}`,
            zIndex: 1000
          });

          // Add click listener to open drawer with single pro
          marker.addListener('click', () => {
            setSelectedProClusterList([[pro]]);
            setIsViewingAllVisible(false);
            setIsProDrawerOpen(true);
          });
        } else {
          // Multiple pros - show cluster count
          iconElement = createClusterIcon(cluster.length);

          marker = new google.maps.marker.AdvancedMarkerElement({
            position: centerCoords,
            map: mapRef.current,
            content: iconElement,
            title: `${cluster.length} Pros at this location`,
            zIndex: 1000
          });

          // Add click listener to open drawer with all pros in cluster
          marker.addListener('click', () => {
            setSelectedProClusterList([cluster]);
            setIsViewingAllVisible(false);
            setIsProDrawerOpen(true);
          });
        }

        return marker;
      }).filter(m => m !== null) as google.maps.marker.AdvancedMarkerElement[];

      availableProMarkersRef.current = newProMarkers;
    }
  }, [availablePros, mapReady, parseCoordinates, createContainerIcon, createClusterIcon, clusterPros]);

  // Update visible clusters when map viewport changes (pan, zoom, resize)
  useEffect(() => {
    if (!mapRef.current || !mapReady) {
      return;
    }

    const updateVisibleClusters = () => {
      const bounds = mapRef.current?.getBounds();
      if (!bounds) return;

      // Update map bounds for vehicle list filtering
      setMapBounds(bounds);

      if (availableProClustersRef.current.length === 0) return;

      // Use clusters from ref (already calculated) and filter by viewport
      const visibleClusters = availableProClustersRef.current.filter(cluster => {
        if (cluster.length === 0) return false;
        const coords = cluster[0].coords;
        return coords && bounds.contains(coords);
      });

      // Only update state if the visible clusters actually changed
      const visibleProNumbers = visibleClusters.flat().map(p => p.pronumber).sort().join(',');

      if (visibleProNumbers !== lastVisibleProNumbersRef.current) {
        lastVisibleProNumbersRef.current = visibleProNumbers;
        setVisibleProClusters(visibleClusters);
      }
    };

    // Use idle event instead of bounds_changed to avoid firing too frequently
    // idle fires after pan/zoom animations complete
    const idleListener = mapRef.current.addListener('idle', updateVisibleClusters);

    // Cleanup listeners
    return () => {
      if (idleListener) google.maps.event.removeListener(idleListener);
    };
  }, [mapReady]);

  // Auto-update selectedProClusterList when viewing all visible and viewport changes
  useEffect(() => {
    if (isViewingAllVisible && visibleProClusters.length > 0) {
      setSelectedProClusterList(visibleProClusters);
    }
  }, [visibleProClusters, isViewingAllVisible]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (clustererRef.current) {
        clustererRef.current.clearMarkers();
      }
      markersRef.current.forEach(marker => { marker.map = null; });
      markersRef.current = [];
      availableProMarkersRef.current.forEach(marker => { marker.map = null; });
      availableProMarkersRef.current = [];
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
      }
      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
      }
    };
  }, []);

  // Set icon size slider in footer
  useEffect(() => {
    const SliderComponent = () => {
      const [localPreviewSize, setLocalPreviewSize] = useState(iconSizeMultiplier);
      const previewTruckSize = 60 * localPreviewSize; // Base size 60px for TruckWithContainer
      const percentage = Math.round(localPreviewSize * 100);
      const [isAnimating, setIsAnimating] = useState(false);
      const [truckPosition, setTruckPosition] = useState(0);
      const [textRevealProgress, setTextRevealProgress] = useState(0);

      // Refs to track animation timers for cleanup
      const textRevealIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
      const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

      // Cleanup timers on unmount
      useEffect(() => {
        return () => {
          if (textRevealIntervalRef.current) clearInterval(textRevealIntervalRef.current);
          if (animationTimeoutRef.current) clearTimeout(animationTimeoutRef.current);
        };
      }, []);

      const handleDoubleClick = () => {
        if (!isAnimating) {
          // Calculate distance to move the truck completely off screen
          // Need to account for the truck starting position and truck width
          const truckWidth = previewTruckSize;
          const distanceToTravel = window.innerWidth + truckWidth + 200; // Extra 200px to ensure fully off screen

          // Always animate the truck
          setIsAnimating(true);
          setTruckPosition(distanceToTravel);

          // Animate text reveal progressively over 6 seconds
          textRevealIntervalRef.current = setInterval(() => {
            setTextRevealProgress((prev) => {
              if (prev >= 100) {
                if (textRevealIntervalRef.current) clearInterval(textRevealIntervalRef.current);
                return 100;
              }
              return prev + 1;
            });
          }, 60); // Reveal text over 6 seconds (100 steps * 60ms)

          // End animation when truck is off screen (12 seconds feels right)
          animationTimeoutRef.current = setTimeout(() => {
            setIsAnimating(false);
            setTruckPosition(0);
            setTextRevealProgress(0);
          }, 12000);

          // Record achievement asynchronously (don't wait for it)
          const userEmail = accounts[0]?.username || 'unknown@example.com';
          api.post('/achievements/record', {
            achievement_id: 'keep_on_truckin',
            email: userEmail
          })
            .then(response => {
              // Show notification and confetti only on first discovery
              if (response.data.first_discovery) {
                console.log('🎉 First time achievement unlocked!');
                setAchievementMessage({ title: "Keep On Truckin'", id: 'keep_on_truckin' });
                setShowAchievementNotification(true);
                celebrateAchievement();
              }
            })
            .catch(error => {
              console.error('Failed to record achievement:', error);
            });
        }
      };

      return (
        <>
          <label htmlFor="icon-size-slider" className="text-sm font-medium text-label whitespace-nowrap">
            Icon Size
          </label>
          <div style={{width: '200px'}}>
            <input
              id="icon-size-slider"
              type="range"
              min="0.5"
              max="2"
              step="0.1"
              value={localPreviewSize}
              onChange={(e) => setLocalPreviewSize(parseFloat(e.target.value))}
              onMouseUp={(e) => setIconSizeMultiplier(parseFloat((e.target as HTMLInputElement).value))}
              onTouchEnd={(e) => setIconSizeMultiplier(parseFloat((e.target as HTMLInputElement).value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700"
            />
            </div>
            {/* Preview truck icon to the right of slider */}
            <div className="flex items-center justify-center" style={{width: '120px', height: '48px', position: 'relative', overflow: isAnimating ? 'visible' : 'hidden'}}>
            {/* "Keep On Truckin'" text that gets revealed */}
            {isAnimating && (
              <div
                style={{
                  position: 'absolute',
                  left: '10px',
                  bottom: '8px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  fontFamily: 'var(--app-font-family)',
                  fontStyle: 'italic',
                  color: '#1e3a8a',
                  zIndex: 1,
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.5px',
                }}
              >
                {"Keep On Truckin'".split('').map((char, index) => {
                  const charPosition = (index / 17) * 100; // 17 characters total
                  const isRevealed = textRevealProgress >= charPosition;
                  return (
                    <span
                      key={index}
                      style={{
                        opacity: isRevealed ? 1 : 0,
                        transition: 'opacity 0.3s ease-in',
                      }}
                    >
                      {char}
                    </span>
                  );
                })}
              </div>
            )}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 62 18"
              width={previewTruckSize}
              height={previewTruckSize}
              className="cursor-pointer select-none"
              style={{
                transform: `translateX(${truckPosition}px)`,
                transition: isAnimating ? 'transform 12s cubic-bezier(0.1, 0, 0.3, 1)' : 'transform 0.1s',
                position: 'relative',
                zIndex: isAnimating ? 9999 : 1,
              }}
              onDoubleClick={handleDoubleClick}
            >
              <g transform="translate(-1.4, -22.39)">
                <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z"
                  fill="#052046"
                  stroke="${strokeColor}"
                  strokeWidth="0.4"/>
                <g transform="translate(0.033063, 0.0104117)">
                  <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                    fill="#052046"
                    stroke="${strokeColor}"
                    strokeWidth="0.4"/>
                </g>
                <g transform="translate(1.68205, 24.8414)">
                  <text x="22px" y="7.756px" style={{fontFamily: 'var(--app-font-family)', fontSize: '8px', fill: 'white', textAnchor: 'middle'}}>{percentage}%</text>
                </g>
              </g>
            </svg>
          </div>

        </>
      );
    };

    setIconSizeSlider(<SliderComponent />);

    return () => {
      setIconSizeSlider(null);
    };
  }, [iconSizeMultiplier, setIconSizeSlider]);


  if (loadError) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-3">
          <MapPinIcon className="w-12 h-12 text-red-500 mx-auto" />
          <h3 className="text-lg font-medium text-heading">Map Load Error</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Failed to load Google Maps. Please check your API key configuration.
          </p>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center space-y-3">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading Google Maps...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Achievement Notification */}
      {showAchievementNotification && createPortal(
        <NotificationContainer position="top-center">
          <Notification
            type="success"
            title="Congrats!"
            message={
              <span>
                You found the <span style={{ color: isDarkMode ? '#93c5fd' : '#1e3a8a', fontWeight: 600 }}>{achievementMessage?.title || "Keep On Truckin'"}</span> Easter Egg
              </span>
            }
            position="top-center"
            showClose={true}
            onClose={() => { setShowAchievementNotification(false); setAchievementMessage(null); }}
            autoDismiss={5000}
          />
        </NotificationContainer>,
        document.body
      )}

      <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''} bg-gray-50 dark:bg-gray-900`}>
      {/* Page Header */}
      <PermissionsContainer componentId="livemap-header" componentName="Live Map Controls">
      <div className={`${styles.pageHeader} bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700`} style={{ position: 'relative' }}>
        <GridLabel areaName="pageHeader" position="top-right" />
        <div className="px-4 py-3">
          {/* Desktop layout - single row */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPinIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
              <h1 className="text-xl font-semibold text-heading">Live Fleet Map</h1>

              {/* Driver Dispatcher TypeAhead */}
              <div className="w-72">
                <TypeAhead
                  options={dispatcherOptions as TypeAheadOption[]}
                  value={selectedDispatchers}
                  onChange={handleDispatcherChange}
                  placeholder="Driver Dispatcher"
                  multiple={true}
                  showSearch={true}
                />
              </div>

              {/* Region TypeAhead */}
              <div className="w-56">
                <TypeAhead
                  options={regionOptions}
                  value={selectedRegions}
                  onChange={handleRegionChange}
                  placeholder="Region"
                  multiple={true}
                  showSearch={true}
                />
              </div>

              {/* Local / OTR toggle */}
              <div className="flex items-center gap-1 flex-shrink-0" role="group" aria-label="Driver type filter">
                {(['LOCAL', 'OTR'] as DriverType[]).map(type => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={selectedDriverTypes.includes(type)}
                    onClick={() => toggleDriverType(type)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                      selectedDriverTypes.includes(type)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
                    }`}
                  >
                    {type === 'LOCAL' ? 'Local' : 'OTR'}
                  </button>
                ))}
              </div>

              {/* Location Search */}
              <div className="relative w-72">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <PermissionAwareInput
                  ref={locationSearchInputRef}
                  type="text"
                  value={locationSearchQuery}
                  onChange={(e) => setLocationSearchQuery(e.target.value)}
                  placeholder="Search location..."
                  className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-panel text-body placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
                {locationSearchQuery && (
                  <button
                    onClick={() => {
                      setLocationSearchQuery('');
                      if (locationSearchMarkerRef.current) {
                        locationSearchMarkerRef.current.map = null;
                        locationSearchMarkerRef.current = null;
                      }
                    }}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                  >
                    <XMarkIcon className="w-4 h-4 text-gray-400" />
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              {/* TEMPORARY TEST BUTTON - Hidden but in place */}
              <button
                onClick={() => {
                  setIsProDrawerOpen(!isProDrawerOpen);
                  setSelectedProClusterList([[]]);  // Empty cluster to allow opening
                }}
                className="px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded hover:bg-purple-700 invisible"
              >
                {isProDrawerOpen ? 'Close' : 'Open'} Pro Drawer (TEST)
              </button>

              {/* Last update time */}
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <ClockIcon className="w-4 h-4 mr-1" />
                Last updated: {lastUpdate.toLocaleTimeString()}
              </div>
            </div>
          </div>

          {/* Mobile layout - multiple rows */}
          <div className="lg:hidden space-y-3">
            {/* Title row */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <MapPinIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                <h1 className="text-lg font-semibold text-heading">Live Fleet Map</h1>
              </div>
              <span className="text-sm text-muted">
                {vehicleMarkers.length} vehicles
              </span>
            </div>

            {/* Driver Dispatcher TypeAhead row */}
            <div className="w-full">
              <TypeAhead
                options={dispatcherOptions as TypeAheadOption[]}
                value={selectedDispatchers}
                onChange={handleDispatcherChange}
                placeholder="Driver Dispatcher"
                multiple={true}
                showSearch={true}
              />
            </div>

            {/* Region + Local/OTR row */}
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <TypeAhead
                  options={regionOptions}
                  value={selectedRegions}
                  onChange={handleRegionChange}
                  placeholder="Region"
                  multiple={true}
                  showSearch={true}
                />
              </div>
              <div className="flex items-center gap-1 flex-shrink-0" role="group" aria-label="Driver type filter">
                {(['LOCAL', 'OTR'] as DriverType[]).map(type => (
                  <button
                    key={type}
                    type="button"
                    aria-pressed={selectedDriverTypes.includes(type)}
                    onClick={() => toggleDriverType(type)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                      selectedDriverTypes.includes(type)
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
                    }`}
                  >
                    {type === 'LOCAL' ? 'Local' : 'OTR'}
                  </button>
                ))}
              </div>
            </div>

            {/* Location Search row */}
            <div className="relative w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <PermissionAwareInput
                ref={locationSearchMobileInputRef}
                type="text"
                value={locationSearchQuery}
                onChange={(e) => setLocationSearchQuery(e.target.value)}
                placeholder="Search location..."
                className="w-full pl-10 pr-10 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-panel text-body placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              />
              {locationSearchQuery && (
                <button
                  onClick={() => {
                    setLocationSearchQuery('');
                    if (locationSearchMarkerRef.current) {
                      locationSearchMarkerRef.current.map = null;
                      locationSearchMarkerRef.current = null;
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded"
                >
                  <XMarkIcon className="w-4 h-4 text-gray-400" />
                </button>
              )}
            </div>

            {/* Last update row */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                <ClockIcon className="w-4 h-4 mr-1" />
                <span>{lastUpdate.toLocaleTimeString()}</span>
              </div>
            </div>

            {/* Statistics row */}
            <div className="flex items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400 font-semibold">
                {vehicleMarkers.length} Active Vehicles - {getDispatcherDisplayText(3)}
              </span>
            </div>

          </div>
        </div>
      </div>
      </PermissionsContainer>

      {/* Status Bar - Desktop only */}
      <PermissionsContainer componentId="livemap-filters" componentName="Map Filters">
      <div className={`${styles.statusBar} hidden lg:block bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700`} style={{ position: 'relative' }}>
        <GridLabel areaName="statusBar" position="top-right" />
        {/* Error message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-2 text-sm">
            {error}
          </div>
        )}

        {/* Statistics and filters bar */}
        <div className="flex items-center justify-between px-4 py-2 text-sm">
          <div className="flex items-center">
            <span className="text-gray-600 dark:text-gray-400 font-semibold">
              {vehicleMarkers.length} Active Vehicles - {getDispatcherDisplayText()}
            </span>
          </div>

          {/* Filter buttons */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Endorsements Popup Chip */}
            <div
              className="relative"
              onMouseEnter={() => setShowEndorsementsPopup(true)}
              onMouseLeave={() => setShowEndorsementsPopup(false)}
            >
              <button
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${
                  [show93k, showHazmat, showTank, showLightTruck, showDaycab, showSleeper].filter(Boolean).length > 0
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-indigo-500'
                    : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-500 hover:ring-gray-400'
                }`}
              >
                <span>Endorsements</span>
                {[show93k, showHazmat, showTank, showLightTruck, showDaycab, showSleeper].filter(Boolean).length > 0 && (
                  <span className="text-indigo-500 dark:text-indigo-400">
                    ({[show93k, showHazmat, showTank, showLightTruck, showDaycab, showSleeper].filter(Boolean).length})
                  </span>
                )}
              </button>
              {showEndorsementsPopup && (
                <div className="absolute top-full left-0 mt-1 p-3 bg-panel rounded-lg shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 z-50 min-w-[280px]">
                  <div className="flex flex-wrap gap-2">
                    <button onClick={() => setShow93k(!show93k)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${show93k ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-purple-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span className="gw-icon" style={{ fontSize: '14px' }}>&#xE9E5;</span><span>93K</span>
                    </button>
                    <button onClick={() => setShowHazmat(!showHazmat)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${showHazmat ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 ring-orange-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span className="gw-icon gw-icon-hazmat" style={{ fontSize: '14px' }}></span><span>Hazmat</span>
                    </button>
                    <button onClick={() => setShowTank(!showTank)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${showTank ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 ring-purple-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span className="gw-icon gw-icon-tank" style={{ fontSize: '14px' }}></span><span>Tank</span>
                    </button>
                    <button onClick={() => setShowLightTruck(!showLightTruck)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${showLightTruck ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 ring-green-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span>&lt;15K</span>
                    </button>
                    <button onClick={() => setShowDaycab(!showDaycab)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${showDaycab ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 ring-blue-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span className="gw-icon gw-icon-daycab" style={{ fontSize: '14px' }}></span><span>Daycab</span>
                    </button>
                    <button onClick={() => setShowSleeper(!showSleeper)} className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${showSleeper ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 ring-indigo-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>
                      <span>Sleeper</span>
                    </button>
                  </div>
                  {[show93k, showHazmat, showTank, showLightTruck, showDaycab, showSleeper].filter(Boolean).length > 0 && (
                    <button onClick={() => { setShow93k(false); setShowHazmat(false); setShowTank(false); setShowLightTruck(false); setShowDaycab(false); setShowSleeper(false); }} className="mt-2 text-xs text-muted hover:text-gray-700 dark:hover:text-gray-200 underline">Clear all</button>
                  )}
                </div>
              )}
            </div>

            {/* Equip Type Popup Chip */}
            <div
              className="relative"
              onMouseEnter={() => setShowEquipTypePopup(true)}
              onMouseLeave={() => setShowEquipTypePopup(false)}
            >
              <button
                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${
                  selectedSizes.length + selectedEquipTypes.length + selectedChassisTypes.length > 0
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-500'
                    : 'bg-white dark:bg-gray-600 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-500 hover:ring-gray-400'
                }`}
              >
                <span>Equip Type</span>
                {selectedSizes.length + selectedEquipTypes.length + selectedChassisTypes.length > 0 && (
                  <span className="text-emerald-500 dark:text-emerald-400">({selectedSizes.length + selectedEquipTypes.length + selectedChassisTypes.length})</span>
                )}
              </button>
              {showEquipTypePopup && (
                <div className="absolute top-full left-0 mt-1 p-3 bg-panel rounded-lg shadow-lg ring-1 ring-gray-200 dark:ring-gray-700 z-50 min-w-[320px]">
                  {equipTypeOptions.sizes.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-muted mb-1.5">Size</div>
                      <div className="flex flex-wrap gap-1.5">
                        {equipTypeOptions.sizes.map(size => (
                          <button key={size} onClick={() => setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size])} className={`px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${selectedSizes.includes(size) ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>{size}'</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {equipTypeOptions.equipTypes.length > 0 && (
                    <div className="mb-3">
                      <div className="text-xs font-medium text-muted mb-1.5">Equipment Type</div>
                      <div className="flex flex-wrap gap-1.5">
                        {equipTypeOptions.equipTypes.map(type => (
                          <button key={type} onClick={() => setSelectedEquipTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])} className={`px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${selectedEquipTypes.includes(type) ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>{type.split(' ').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {equipTypeOptions.chassisTypes.length > 0 && (
                    <div>
                      <div className="text-xs font-medium text-muted mb-1.5">Chassis Type</div>
                      <div className="flex flex-wrap gap-1.5">
                        {equipTypeOptions.chassisTypes.map(type => (
                          <button key={type} onClick={() => setSelectedChassisTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type])} className={`px-2 py-1 rounded text-xs font-medium ring-1 ring-inset transition-all cursor-pointer ${selectedChassisTypes.includes(type) ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 ring-emerald-500' : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300 ring-gray-300 dark:ring-gray-600 hover:ring-gray-400'}`}>{type.split(' ').map(word => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}</button>
                        ))}
                      </div>
                    </div>
                  )}
                  {selectedSizes.length + selectedEquipTypes.length + selectedChassisTypes.length > 0 && (
                    <button onClick={() => { setSelectedSizes([]); setSelectedEquipTypes([]); setSelectedChassisTypes([]); }} className="mt-2 text-xs text-muted hover:text-gray-700 dark:hover:text-gray-200 underline">Clear all</button>
                  )}
                  {equipTypeOptions.sizes.length === 0 && equipTypeOptions.equipTypes.length === 0 && equipTypeOptions.chassisTypes.length === 0 && (
                    <div className="text-xs text-gray-400 dark:text-gray-500 italic">No equipment data available</div>
                  )}
                </div>
              )}
            </div>

            {/* Separator */}
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-600 mx-1"></div>

            <button
              onClick={() => setShowInProgress(!showInProgress)}
              className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 ${
                showInProgress
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
              }`}
            >
              In Progress ({stats.inProgress})
            </button>
            <button
              onClick={() => setShowDispatched(!showDispatched)}
              className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 ${
                showDispatched
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
              }`}
            >
              Dispatched ({stats.dispatched})
            </button>
            <button
              onClick={() => setShowAvailable(!showAvailable)}
              className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 ${
                showAvailable
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
              }`}
            >
              Available ({stats.available})
            </button>
            <button
              onClick={() => setShowOutOfService(!showOutOfService)}
              className={`px-2 py-1 text-xs rounded transition-colors whitespace-nowrap flex-shrink-0 ${
                showOutOfService
                  ? 'bg-green-600 text-white'
                  : 'bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-500'
              }`}
            >
              Out Of Service ({stats.outOfService})
            </button>
          </div>
        </div>
      </div>
      </PermissionsContainer>

      {/* Main Content */}
      <PermissionsContainer componentId="livemap-content" componentName="Map Content">
      <div className={styles.mainContent} style={{ position: 'relative' }}>
        <GridLabel areaName="mainContent" position="top-right" />
        {/* Info Drawer - Left side */}
        <div className={`${styles.infoDrawer} ${!selectedVehicleId || isVehicleInfoDrawerCollapsed ? styles.infoDrawerCollapsed : ''} hidden lg:block relative`}>
          <VehicleInfoDrawer
            selectedVehicle={selectedVehicle}
            setSelectedVehicleId={setSelectedVehicleId}
            setIsInfoWindowMinimized={setIsInfoWindowMinimized}
            loadingDispatches={loadingDispatches}
            currentDispatches={currentDispatches}
            routeInfo={routeInfo}
            selectedDispatchRoute={selectedDispatchRoute}
            setSelectedDispatchRoute={setSelectedDispatchRoute}
            isCollapsed={isVehicleInfoDrawerCollapsed}
            setIsCollapsed={setIsVehicleInfoDrawerCollapsed}
            availablePros={availablePros}
            setIsProDrawerOpen={setIsProDrawerOpen}
            setSelectedProClusterList={setSelectedProClusterList}
            setIsViewingAllVisible={setIsViewingAllVisible}
            visibleProClusters={visibleProClusters}
            onParkingLocationClick={handleParkingLocationClick}
            driverMovesData={driverMovesData}
            loadingDriverMoves={loadingDriverMoves}
            showCompletedMoves={showCompletedMoves}
            setShowCompletedMoves={setShowCompletedMoves}
            onMoveProClick={handleMoveProClick}
            onMoveProDetailsClick={handleMoveProDetailsClick}
          />
        </div>

        {/* Map */}
        <div ref={mapContainerRef} className={styles.map}>
          {loading && !driverLocations.length ? (
            <div className="absolute inset-0 flex items-center justify-center bg-panel z-10">
              <div className="text-center space-y-3">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading vehicle locations...</p>
                <div className="text-xs text-gray-500 dark:text-gray-500 space-y-1">
                  <p>Fetching real-time vehicle location feed...</p>
                </div>
              </div>
            </div>
          ) : driverLocations.length === 0 && !loading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-panel z-10">
              <div className="text-center space-y-3 max-w-md">
                <MapPinIcon className="w-12 h-12 text-gray-400 mx-auto" />
                <h3 className="text-lg font-medium text-heading">No Active Vehicles</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No vehicles with location data found.
                </p>
                <button
                  onClick={fetchData}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <GoogleMap
              key={`map-${isDarkMode ? 'dark' : 'light'}`}
              mapContainerStyle={mapContainerStyle}
              center={defaultCenter}
              zoom={11}
              options={getMapOptions(isDarkMode, mapType)}
              onLoad={onMapLoad}
            >
              {/* Markers are now managed by MarkerClusterer via useEffect */}
              {/* Satellite/Map Toggle is added as a native Google Maps control in onMapLoad */}

              {/* Legend - Bottom left corner */}
              <div className="absolute bottom-8 left-4 bg-panel rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-10">
                <div className="p-3">
                  <div className="flex gap-6">
                    {/* Status Section */}
                    <div>
                      <div className="text-xs font-semibold text-label mb-2">Status</div>
                      <div className="space-y-1.5">
                        {/* Available - Green square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-green-600 rounded border border-gray-500"></div>
                          <span className="text-xs text-label">Available</span>
                        </div>
                        {/* In Progress - Blue square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-blue-600 rounded border border-gray-500"></div>
                          <span className="text-xs text-label">In Progress</span>
                        </div>
                        {/* Dispatched - Grey square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-gray-600 rounded border border-gray-500"></div>
                          <span className="text-xs text-label">Dispatched</span>
                        </div>
                        {/* Dispatched Not Started - Orange square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-orange-500 rounded border border-gray-500"></div>
                          <span className="text-xs text-label">Dispatched Not Started</span>
                        </div>
                        {/* Not Working - Light Yellow square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded border border-gray-500" style={{ backgroundColor: '#FFFAA0' }}></div>
                          <span className="text-xs text-label">Not Working</span>
                        </div>
                        {/* Out of Service - Red square */}
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-red-600 rounded border border-gray-500"></div>
                          <span className="text-xs text-label">Out of Service</span>
                        </div>
                      </div>
                    </div>

                    {/* Load Type Section */}
                    <div>
                      <div className="text-xs font-semibold text-label mb-2">Load Type</div>
                      <div className="space-y-1.5">
                    {/* Loaded - Truck with container */}
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 18" width="32" height="16">
                        <g transform="matrix(1,0,0,1,-1.4,-22.39)">
                          <path d="M43.03,38.59L44.6,34.39L44.6,24.99C44.6,24.66 44.33,24.39 44,24.39L43.6,24.39C43.6,24.39 1.41,24.005 1.41,24.335L1.4,34.99C1.4,35.32 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4Z" fill="#4B5563"/>
                          <g transform="matrix(1,0,0,1,0.033063,0.0104117)">
                            <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z" fill="#4B5563"/>
                          </g>
                        </g>
                      </svg>
                      <span className="text-xs text-label">Loaded</span>
                    </div>
                    {/* Bare - Truck with chassis */}
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 62 15" width="32" height="16">
                        <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 1.472,34.325 1.472,34.325C1.472,34.655 1.67,35.59 2,35.59L4.82,35.59C4.56,35.99 4.4,36.47 4.4,36.99C4.4,38.42 5.57,39.59 7,39.59C7.81,39.59 8.52,39.21 9,38.63C9.48,39.21 10.19,39.59 11,39.59C11.81,39.59 12.52,39.21 13,38.63C13.48,39.21 14.19,39.59 15,39.59C16.43,39.59 17.6,38.42 17.6,36.99C17.6,36.47 17.44,35.99 17.18,35.59L19.4,35.59L19.4,36.99C19.4,37.32 19.67,37.59 20,37.59L31,37.59C31.33,37.59 31.6,37.32 31.6,36.99L31.6,35.59L34.82,35.59C34.56,35.99 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM56.16,31.37C56.16,31.37 56.16,31.39 56.17,31.4L53.6,31.4L53.6,30.6L55.77,30.6C55.92,30.85 56.05,31.11 56.16,31.37ZM7,38.4C6.23,38.4 5.6,37.77 5.6,37C5.6,36.23 6.23,35.6 7,35.6C7.77,35.6 8.4,36.23 8.4,37C8.4,37.77 7.77,38.4 7,38.4ZM11,38.4C10.23,38.4 9.6,37.77 9.6,37C9.6,36.23 10.23,35.6 11,35.6C11.77,35.6 12.4,36.23 12.4,37C12.4,37.77 11.77,38.4 11,38.4ZM16.4,37C16.4,37.77 15.77,38.4 15,38.4C14.23,38.4 13.6,37.77 13.6,37C13.6,36.23 14.23,35.6 15,35.6C15.77,35.6 16.4,36.23 16.4,37ZM30.4,36.4L20.6,36.4L20.6,35.6L30.4,35.6L30.4,36.4ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                          transform="translate(-1.47155, -25.4004)"
                          fill="#4B5563"
                          stroke="${strokeColor}"
                          strokeWidth="0.4"/>
                      </svg>
                      <span className="text-xs text-label">Bare</span>
                    </div>
                    {/* No Load - Truck empty */}
                    <div className="flex items-center gap-2">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 29 15" width="22" height="16">
                        <path d="M62.12,32.41L57.48,31.48C57.45,31.39 57.42,31.29 57.39,31.2C57.34,31.05 57.27,30.91 57.21,30.77C57.21,30.75 57.2,30.73 57.19,30.72C56.82,29.88 56.29,29.09 55.6,28.43C54.62,27.5 53.41,26.86 52.08,26.6L46.12,25.41C45.94,25.38 45.76,25.42 45.62,25.53C45.48,25.64 45.4,25.81 45.4,25.99L45.4,34.39L44.6,34.39C44.6,34.06 34.508,34.268 34.508,34.268C34.248,34.668 34.4,36.47 34.4,36.99C34.4,38.42 35.57,39.59 37,39.59C37.81,39.59 38.52,39.21 39,38.63C39.48,39.21 40.19,39.59 41,39.59C41.81,39.59 42.56,39.19 43.03,38.59L55.98,38.59C56.47,39.21 57.19,39.59 58,39.59C58.81,39.59 59.53,39.21 60.02,38.59L62,38.59C62.33,38.59 62.6,38.32 62.6,37.99L62.6,32.99C62.6,32.7 62.4,32.46 62.12,32.4L62.12,32.41ZM54.759,29.641C54.795,29.701 55.271,30.101 55.271,30.101L55.551,30.485L55.848,31.013C55.848,31.013 56.091,31.592 56.093,31.595L51.561,31.519L51.507,29.086C51.507,29.086 52.623,29.018 53.165,29.111C53.445,29.158 54.236,29.33 54.236,29.33L54.759,29.641ZM37,38.4C36.23,38.4 35.6,37.77 35.6,37C35.6,36.23 36.23,35.6 37,35.6C37.77,35.6 38.4,36.23 38.4,37C38.4,37.77 37.77,38.4 37,38.4ZM41,38.4C40.23,38.4 39.6,37.77 39.6,37C39.6,36.23 40.23,35.6 41,35.6C41.77,35.6 42.4,36.23 42.4,37C42.4,37.77 41.77,38.4 41,38.4ZM56.79,37.7C56.67,37.49 56.6,37.25 56.6,37C56.6,36.23 57.23,35.6 58,35.6C58.77,35.6 59.4,36.23 59.4,37C59.4,37.24 59.33,37.49 59.21,37.7C58.7,38.58 57.3,38.58 56.79,37.7Z"
                          transform="translate(-34.3558, -25.4004)"
                          fill="#4B5563"
                          stroke="${strokeColor}"
                          strokeWidth="0.4"/>
                      </svg>
                      <span className="text-xs text-label">No Load</span>
                    </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </GoogleMap>
          )}

          {/* Vehicle Hover Tooltip */}
          {hoveredVehicle && (
            <div
              className="fixed z-[9999] pointer-events-none"
              style={{
                left: hoveredVehicle.position.x,
                top: hoveredVehicle.position.y - 8,
                transform: 'translate(-50%, -100%)'
              }}
            >
              <div className="bg-gray-900 text-white text-xs rounded-lg px-3 py-2 shadow-lg whitespace-nowrap">
                {hoveredVehicle.info.unitNumber && (
                  <div className="font-semibold">{hoveredVehicle.info.unitNumber}<MuleBadge isMule={hoveredVehicle.info.isMule} /></div>
                )}
                {hoveredVehicle.info.driverName && (
                  <div className="text-gray-300">{hoveredVehicle.info.driverName}</div>
                )}
                <div className="mt-1 pt-1 border-t border-gray-700 space-y-0.5">
                  {hoveredVehicle.info.proNumber && !hoveredVehicle.info.isOOS && !hoveredVehicle.info.isNotWorking && (
                    <div>Current: <span className="text-blue-400">{hoveredVehicle.info.proNumber}</span></div>
                  )}
                  <div>Completed: <span className="text-green-400">{hoveredVehicle.info.completedToday}</span></div>
                  <div>Remaining: <span className="text-orange-400">{hoveredVehicle.info.remainingDispatches}</span></div>
                  {hoveredVehicle.info.isOOS && (
                    <div className="text-red-400 font-medium">Out of Service</div>
                  )}
                  {hoveredVehicle.info.isNotWorking && !hoveredVehicle.info.isOOS && (
                    <div className="text-yellow-400 font-medium">Not Working</div>
                  )}
                </div>
                {/* Endorsements */}
                <div className="mt-1 pt-1 border-t border-gray-700">
                  <div className="flex flex-wrap gap-1.5 items-center">
                    {hoveredVehicle.info.has93k && (
                      <span className="gw-icon text-purple-400" style={{ fontSize: '16px' }} title="93K Permit">&#xE9E5;</span>
                    )}
                    {hoveredVehicle.info.hasHazmat && (
                      <span className="gw-icon gw-icon-hazmat text-orange-400" style={{ fontSize: '16px' }} title="Hazmat"></span>
                    )}
                    {hoveredVehicle.info.hasTank && (
                      <span className="gw-icon gw-icon-tank text-purple-400" style={{ fontSize: '16px' }} title="Tank"></span>
                    )}
                    {hoveredVehicle.info.isLightTruck && (
                      <span title="<15K lbs">
                        <svg viewBox="0 0 32 20" width="24" height="15" fill="currentColor" className="text-green-400">
                          <rect x="1" y="1" width="30" height="18" rx="3" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                          <text x="16" y="14" textAnchor="middle" fontFamily="var(--app-font-family)" fontSize="11" fontWeight="bold">&lt;15K</text>
                        </svg>
                      </span>
                    )}
                    {hoveredVehicle.info.isDaycab ? (
                      <span className="gw-icon gw-icon-daycab text-blue-400" style={{ fontSize: '16px' }} title="Daycab"></span>
                    ) : (
                      <span title="Sleeper">
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="text-indigo-400">
                          <path d="M2,14H24v8H22V19H2v3H0V2H2Zm3.5-2A2.5,2.5,0,1,0,3,9.5,2.5,2.5,0,0,0,5.5,12ZM21,6H12A3,3,0,0,0,9,9v3H24V9A3,3,0,0,0,21,6Z"/>
                        </svg>
                      </span>
                    )}
                  </div>
                </div>
                {/* Arrow */}
                <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
              </div>
            </div>
          )}
        </div>

        {/* Available Pros Drawer - Right side */}
        <div className={`${styles.vehicleList} ${!isProDrawerOpen ? styles.vehicleListCollapsed : ''} hidden lg:block relative overflow-hidden`}>
          {isProDrawerOpen && selectedProClusterList && (() => {
            // Flatten all clusters and re-sort by original backend order
            const allProsFromClusters = selectedProClusterList.flat();
            const sortedPros = allProsFromClusters.sort((a, b) => {
              const indexA = availablePros.findIndex(p => p.pronumber === a.pronumber);
              const indexB = availablePros.findIndex(p => p.pronumber === b.pronumber);
              return indexA - indexB;
            });

            return (
            <div className="h-full w-96 bg-panel shadow-2xl flex flex-col border-l border-gray-200 dark:border-gray-700">
              {/* Header */}
              <div className="border-b border-gray-200 dark:border-gray-700 bg-panel">
                <div className="flex items-center justify-between p-4">
                  <h2 className="text-lg font-semibold text-heading">
                    Available Pros ({sortedPros.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setShowProSearch(!showProSearch);
                        if (showProSearch) setProSearchQuery('');
                      }}
                      className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Search Pros"
                    >
                      {showProSearch ? (
                        <XMarkIcon className="w-5 h-5" />
                      ) : (
                        <MagnifyingGlassIcon className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setIsProDrawerOpen(false);
                        setSelectedProClusterList(null);
                        setIsViewingAllVisible(false);
                        setShowProSearch(false);
                        setProSearchQuery('');
                      }}
                      className="p-1 text-muted hover:text-gray-700 dark:hover:text-gray-200"
                      aria-label="Close"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>
                </div>
                {showProSearch && (
                  <div className="px-4 pb-3">
                    <PermissionAwareInput
                      type="text"
                      value={proSearchQuery}
                      onChange={(e) => setProSearchQuery(e.target.value)}
                      placeholder="Search by PRO, customer, or location..."
                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white dark:placeholder-gray-400"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Pro List */}
              <div className="flex-1 overflow-y-auto">
                {sortedPros
                  .filter((pro: any) => {
                    if (!proSearchQuery) return true;
                    const query = proSearchQuery.toLowerCase();
                    return (
                      String(pro.pronumber).toLowerCase().includes(query) ||
                      pro.customerName?.toLowerCase().includes(query) ||
                      pro.originName?.toLowerCase().includes(query) ||
                      pro.destName?.toLowerCase().includes(query) ||
                      pro.container?.toLowerCase().includes(query)
                    );
                  })
                  // Backend already provides the correct sort order, so we don't sort here
                  .map((pro, index) => {
                  const coords = pro.coords || parseCoordinates(pro.originCoordinates);
                  const isSelected = selectedDispatchRoute?.proNumber === String(pro.pronumber);

                  // Find the original index in availablePros for debugging
                  const originalIndex = availablePros.findIndex(p => p.pronumber === pro.pronumber);

                  return (
                    <div
                      key={pro.pronumber || index}
                      onClick={() => {
                        // Draw route from origin to destination
                        if (pro.originCoordinates && pro.destCoordinates) {
                          // @ts-ignore - Available Pros don't have geofence data
                          setSelectedDispatchRoute({
                            proNumber: String(pro.pronumber),
                            isInProgress: false,
                            isCompleted: false,
                            skipZoom: true, // Don't zoom when clicking available pros
                            legs: [{
                              origcoordinates: String(pro.originCoordinates),
                              destcoordinates: String(pro.destCoordinates),
                              originName: String(pro.originName || ''),
                              destName: String(pro.destName || ''),
                              origgeofencetype: '',
                              origgeofenceradius: 0,
                              destgeofencetype: '',
                              destgeofenceradius: 0,
                              isComplete: false
                            }]
                          });
                          // The route drawing effect will handle panning to center
                        } else if (coords && mapRef.current) {
                          // If no destination coordinates, just pan to origin
                          mapRef.current.panTo(coords);
                        }
                      }}
                      className={`p-4 border-b border-gray-200 dark:border-gray-700 cursor-pointer ${
                        isSelected
                          ? 'bg-gray-100 dark:bg-gray-700'
                          : 'hover:bg-highlight'
                      }`}
                    >
                      <div className="flex items-stretch justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedProNumber(pro.pronumber);
                                setIsProDetailsModalOpen(true);
                              }}
                              className="text-sm font-semibold text-gray-700 dark:text-gray-200 hover:underline"
                              title="View order details"
                            >
                              {pro.pronumber}
                            </button>
                            <a
                              href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pro.pronumber}&searchtype=pronumber`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-muted hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                              title="Open in CMS v1"
                            >
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          </div>

                          {pro.customerName && (
                            <div className="text-sm text-heading font-medium mb-1">
                              {pro.customerName}
                            </div>
                          )}

                          {pro.originName && (
                            <div className="text-sm text-gray-600 dark:text-gray-300 mb-1">
                              <MapPinIcon className="w-4 h-4 inline-block mr-1" />
                              {pro.originName}
                            </div>
                          )}

                          {pro.originCityState && (
                            <div className="text-xs text-muted ml-5">
                              {pro.originCityState}
                            </div>
                          )}

                          {pro.destName && pro.destName !== '' && (
                            <>
                              <div className="text-sm text-gray-600 dark:text-gray-300 mb-1 mt-1">
                                <MapPinIcon className="w-4 h-4 inline-block mr-1" />
                                {pro.destName}
                              </div>
                              {pro.destName !== 'TBD' && pro.destCityState && pro.destCityState !== '-, -' && (
                                <div className="text-xs text-muted ml-5 mb-1">
                                  {pro.destCityState}
                                </div>
                              )}
                            </>
                          )}

                          {/* Appointment Times */}
                          {pro.destApptDate && pro.destApptDate !== '' && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              Appt: {pro.destApptDate}
                              {pro.destApptTime1 && ` ${pro.destApptTime1}`}
                              {pro.destApptTime2 && `-${pro.destApptTime2}`}
                            </div>
                          )}

                          {/* Per Diem and Cutoff for Exports */}
                          {pro.moveType && (pro.moveType === 'EXPORT' || pro.moveType === 'RAIL TO PORT') && (pro.perDiemDate || pro.cutoffDatetime) && (() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);
                            const parts = [];

                            if (pro.perDiemDate && pro.perDiemDate !== '') {
                              // Parse MM/DD format
                              const [month, day] = pro.perDiemDate.split('/').map(Number);
                              const perDiemDate = new Date(today.getFullYear(), month - 1, day);
                              const isPastOrToday = perDiemDate <= today;
                              parts.push(
                                <span key="perdiem" className={isPastOrToday ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                                  Per Diem: {pro.perDiemDate}
                                </span>
                              );
                            }

                            if (pro.cutoffDatetime && pro.cutoffDatetime !== '') {
                              // Parse MM/DD HH:mm format
                              const [datePart, timePart] = pro.cutoffDatetime.split(' ');
                              const [month, day] = datePart.split('/').map(Number);
                              const [hours, minutes] = timePart.split(':').map(Number);
                              const cutoffDate = new Date(today.getFullYear(), month - 1, day, hours, minutes);
                              const tomorrow = new Date();
                              tomorrow.setDate(tomorrow.getDate() + 1);
                              tomorrow.setHours(0, 0, 0, 0);
                              const isPastOrNow = cutoffDate < tomorrow;
                              parts.push(
                                <span key="cutoff" className={isPastOrNow ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>
                                  Cutoff: {pro.cutoffDatetime}
                                </span>
                              );
                            }

                            if (parts.length === 0) return null;

                            return (
                              <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                {parts.reduce((acc, part, idx) => {
                                  if (idx === 0) return [part];
                                  return [...acc, ' | ', part];
                                }, [] as any[])}
                              </div>
                            );
                          })()}

                          {/* Per Diem for Imports */}
                          {pro.moveType === 'IMPORT' && pro.perDiemDate && pro.perDiemDate !== '' && (() => {
                            const today = new Date();
                            today.setHours(0, 0, 0, 0);

                            // Parse MM/DD format with current year
                            const [month, day] = pro.perDiemDate.split('/').map(Number);
                            const date = new Date(today.getFullYear(), month - 1, day);
                            const isPastOrToday = date <= today;

                            return (
                              <div className={`text-xs mt-1 ${isPastOrToday ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-gray-600 dark:text-gray-400'}`}>
                                Per Diem: {pro.perDiemDate}
                              </div>
                            );
                          })()}

                          {/* Equipment and Chassis Type */}
                          {pro.size && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {pro.size}' {pro.equipType?.split(' ').map((word: string) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')}
                              {pro.chassisType && ` | ${pro.chassisType.split(' ').map((word: string) => word.charAt(0) + word.slice(1).toLowerCase()).join(' ')} Chassis`}
                            </div>
                          )}

                          {/* SCAC and Chassis ID */}
                          {(pro.scac || pro.chassisId) && (
                            <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                              {pro.scac && <span>SCAC: {pro.scac}</span>}
                              {pro.scac && pro.chassisId && <span> | </span>}
                              {pro.chassisId && <span>Chassis: {pro.chassisId}</span>}
                            </div>
                          )}

                          {pro.distance !== undefined && (
                            <div className="text-xs text-green-600 dark:text-green-400 mt-2">
                              {pro.distance} miles away
                            </div>
                          )}
                        </div>

                        <div className="ml-2 flex flex-col items-end justify-start">
                          {/* Move Type Badge */}
                          {pro.moveType && (
                            <span className={`mb-auto px-2 py-0.5 rounded text-xs font-medium ${
                              pro.moveType === 'IMPORT'
                                ? 'bg-info text-info-fg'
                                : pro.moveType === 'EXPORT'
                                ? 'bg-ok text-ok-fg'
                                : pro.moveType === 'RAIL TO PORT'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                            }`}>
                              {pro.moveType}
                            </span>
                          )}

                          {/* Restriction Icons - Bottom Right */}
                          {(pro.hazmat === 'Y' || pro.isTank === 'Y' || pro.overweightPermitRequired === 'Y' || pro.chassisType?.includes('93K') || pro.chassisType?.toLowerCase().includes('triaxle')) && (
                            <div className="flex items-center gap-1.5">
                              {pro.hazmat === 'Y' && (
                                <span className="flaticon flaticon-warning text-orange-500 cursor-help" style={{ fontSize: '20px' }} title="Hazmat"></span>
                              )}
                              {pro.isTank === 'Y' && (
                                <span className="flaticon flaticon-oil-tank text-purple-600 cursor-help" style={{ fontSize: '20px' }} title="Tank"></span>
                              )}
                              {pro.overweightPermitRequired === 'Y' && (
                                <span className="flaticon flaticon-weight text-heading cursor-help" style={{ fontSize: '20px' }} title="Overweight Permit Required"></span>
                              )}
                              {pro.chassisType?.includes('93K') && (
                                <span className="gw-icon cursor-help dark:text-gray-200 text-gray-800" style={{ fontSize: '20px' }} title="93K Triaxle Chassis">
                                  &#xE9E5;
                                </span>
                              )}
                              {!pro.chassisType?.includes('93K') && pro.chassisType?.toLowerCase().includes('triaxle') && (
                                <span className="gw-icon cursor-help dark:text-gray-200 text-gray-800" style={{ fontSize: '20px' }} title="Triaxle Chassis">
                                  &#xE9E1;
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            );
          })()}
        </div>

        {/* Vehicle List - Right side */}
        <div className={`${styles.vehicleList} ${!showDesktopSidebar || isProDrawerOpen ? styles.vehicleListCollapsed : ''} hidden lg:block relative overflow-hidden`}>
          {/* Collapse/Expand Handle */}
          <button
            onClick={() => setShowDesktopSidebar(!showDesktopSidebar)}
            className={`absolute -left-7 top-[18px] z-20 bg-white dark:bg-gray-800 border border-r-0 border-gray-200 dark:border-gray-700 rounded-l-lg py-2 px-1.5 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors shadow-sm ${
              isProDrawerOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'
            }`}
            aria-label={showDesktopSidebar ? "Collapse sidebar" : "Expand sidebar"}
          >
            <ChevronRightIcon
              className={`w-4 h-4 text-gray-600 dark:text-gray-400 ${
                showDesktopSidebar ? 'rotate-0' : 'rotate-180'
              }`}
            />
          </button>

          {/* Sidebar Content */}
          <div className={`h-full bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 overflow-hidden ${
            showDesktopSidebar && !isProDrawerOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}>
            <div className="h-full flex flex-col overflow-hidden">
              {/* Sticky header */}
              <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 bg-panel">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-heading">
                    Vehicle List ({vehicleMarkers.length})
                  </h2>
                  <div className="flex items-center gap-2">
                    <Tooltip content={isListLocked ? "Unlock Driver List" : "Lock Driver List"}>
                      <button
                        onClick={() => setIsListLocked(!isListLocked)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isListLocked
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                        aria-label={isListLocked ? "Unlock vehicle list" : "Lock vehicle list"}
                      >
                        {isListLocked ? (
                          <LockClosedIcon className="w-5 h-5" />
                        ) : (
                          <LockOpenIcon className="w-5 h-5" />
                        )}
                      </button>
                    </Tooltip>
                    <button
                      onClick={() => {
                        setShowSearch(!showSearch);
                        if (showSearch) setSearchQuery('');
                      }}
                      className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                      aria-label="Search vehicles"
                    >
                      {showSearch ? (
                        <XMarkIcon className="w-5 h-5" />
                      ) : (
                        <MagnifyingGlassIcon className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
                {showSearch && (
                  <div className="mt-3">
                    <PermissionAwareInput
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by unit number or name..."
                      className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white dark:placeholder-gray-400"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Scrollable vehicle list */}
              <div className="flex-1 overflow-y-auto">
                {vehicleMarkers.length === 0 ? (
                  <div className="p-4 text-center text-muted">
                    No vehicles to display
                  </div>
                ) : (
                  <>
                    {/* In View Section - only show section header when not locked */}
                    {!isListLocked && (
                      <div className="sticky top-0 z-10 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 border-b border-rule">
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-300">In View ({vehiclesInView.length})</span>
                      </div>
                    )}
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                    {vehiclesInView.map((vehicle) => (
                    <button
                      key={vehicle.uniqueKey}
                      onClick={() => selectAndPanToVehicle(vehicle.id)}
                      className={`w-full px-4 pt-4 pb-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                        selectedVehicleId === vehicle.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <div
                              className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                                  ? 'bg-red-500'
                                  : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                                  ? ''
                                  : vehicle.dispatchInfo?.proNumber && !vehicle.dispatchInfo?.isCompleted
                                  ? 'bg-blue-500'
                                  : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                                  ? 'bg-orange-500'
                                  : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                                  ? 'bg-gray-500'
                                  : 'bg-green-500'
                              }`}
                              style={vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING' && vehicle.dispatchInfo?.v1Status?.toUpperCase() !== 'OUT OF SERVICE' ? { backgroundColor: '#FFFAA0' } : undefined}
                            />
                            <h3 className="font-medium text-heading text-sm truncate">
                              {vehicle.dispatchInfo?.unitNumber && vehicle.dispatchInfo?.driverName
                                ? `${vehicle.dispatchInfo.unitNumber} - ${vehicle.dispatchInfo.driverName}`
                                : vehicle.dispatchInfo?.driverName || vehicle.dispatchInfo?.unitNumber || vehicle.name}<MuleBadge isMule={vehicle.dispatchInfo?.isMule} />
                            </h3>
                          </div>
                          {vehicle.dispatchInfo?.proNumber ? (
                            <div className="mt-1.5 px-2 py-1.5 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                              <p className="text-xs text-blue-700 dark:text-blue-300">
                                Pro: <span className="font-semibold">{vehicle.dispatchInfo.proNumber}</span>
                              </p>
                              {vehicle.dispatchInfo?.originName && (
                                <div className="mt-1">
                                  <p className="text-xs text-blue-800 dark:text-blue-200 truncate flex items-center gap-1">
                                    {vehicle.dispatchInfo.originName}
                                    {!vehicle.dispatchInfo.originCoordinates && (
                                      <ExclamationCircleIcon className="w-3 h-3 text-amber-600 flex-shrink-0" title="Invalid Location" />
                                    )}
                                  </p>
                                  {(vehicle.dispatchInfo?.originArrivalDateTime || vehicle.dispatchInfo?.originDepartDateTime) && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400">
                                      {vehicle.dispatchInfo?.originArrivalDateTime && (
                                        <span>Arv: {new Date(vehicle.dispatchInfo.originArrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                      )}
                                      {vehicle.dispatchInfo?.originArrivalDateTime && vehicle.dispatchInfo?.originDepartDateTime && <span> | </span>}
                                      {vehicle.dispatchInfo?.originDepartDateTime && (
                                        <span>Dep: {new Date(vehicle.dispatchInfo.originDepartDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              )}
                              {vehicle.dispatchInfo?.destinationName && (
                                <div className="mt-0.5">
                                  <p className="text-xs text-blue-800 dark:text-blue-200 truncate flex items-center gap-1">
                                    {vehicle.dispatchInfo.destinationName}
                                    {!vehicle.dispatchInfo.destinationCoordinates && (
                                      <ExclamationCircleIcon className="w-3 h-3 text-amber-600 flex-shrink-0" title="Invalid Location" />
                                    )}
                                  </p>
                                  {(vehicle.dispatchInfo?.arrivalDateTime || vehicle.dispatchInfo?.departDateTime) && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400">
                                      {vehicle.dispatchInfo?.arrivalDateTime && (
                                        <span>Arv: {new Date(vehicle.dispatchInfo.arrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                      )}
                                      {vehicle.dispatchInfo?.arrivalDateTime && vehicle.dispatchInfo?.departDateTime && <span> | </span>}
                                      {vehicle.dispatchInfo?.departDateTime && (
                                        <span>Dep: {new Date(vehicle.dispatchInfo.departDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              {vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                                ? 'Out Of Service'
                                : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                                ? 'Not Working'
                                : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                                ? 'Not Started'
                                : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                                ? 'Dispatched'
                                : 'No Dispatch'}
                            </p>
                          )}
                          {/* Endorsement Icons */}
                          <div className="flex gap-1.5 mt-2 items-center px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                            {vehicle.dispatchInfo?.has93k && (
                              <Tooltip content="93K Permit">
                                <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}>&#xE9E5;</span>
                              </Tooltip>
                            )}
                            {vehicle.dispatchInfo?.hasHazmat && (
                              <Tooltip content="Hazmat">
                                <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '14px' }}></span>
                              </Tooltip>
                            )}
                            {vehicle.dispatchInfo?.hasTank && (
                              <Tooltip content="Tank">
                                <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}></span>
                              </Tooltip>
                            )}
                            {vehicle.dispatchInfo?.isLightTruck && (
                              <Tooltip content="<15K lbs">
                                <svg viewBox="0 0 32 20" width="18" height="11" fill="currentColor" className="text-green-600 dark:text-green-400">
                                  <rect x="1" y="1" width="30" height="18" rx="3" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                                  <text x="16" y="14" textAnchor="middle" fontFamily="var(--app-font-family)" fontSize="11" fontWeight="bold">&lt;15K</text>
                                </svg>
                              </Tooltip>
                            )}
                            {vehicle.dispatchInfo?.isDaycab ? (
                              <Tooltip content="Daycab">
                                <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '14px' }}></span>
                              </Tooltip>
                            ) : (
                              <Tooltip content="Sleeper">
                                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-indigo-600 dark:text-indigo-400">
                                  <path d="M2,14H24v8H22V19H2v3H0V2H2Zm3.5-2A2.5,2.5,0,1,0,3,9.5,2.5,2.5,0,0,0,5.5,12ZM21,6H12A3,3,0,0,0,9,9v3H24V9A3,3,0,0,0,21,6Z"/>
                                </svg>
                              </Tooltip>
                            )}
                          </div>
                          {/* Completed / Remaining counts for In View */}
                          <div className="flex mt-2 px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                            {vehicle.dispatchInfo?.unitNumber ? (
                              <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="done" />} placement="left">
                                <div className="w-1/2 text-xs text-muted cursor-help">
                                  <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                                </div>
                              </Tooltip>
                            ) : (
                              <div className="w-1/2 text-xs text-muted">
                                <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                              </div>
                            )}
                            {vehicle.dispatchInfo?.unitNumber ? (
                              <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="remaining" />} placement="left">
                                <div className="w-1/2 text-xs text-muted cursor-help">
                                  <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                                </div>
                              </Tooltip>
                            ) : (
                              <div className="w-1/2 text-xs text-muted">
                                <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                              </div>
                            )}
                          </div>
                        </div>
                        <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                      </div>
                    </button>
                    ))}
                    </div>

                    {/* Outside View Section */}
                    {vehiclesOutsideView.length > 0 && (
                      <>
                        <div className="sticky top-0 z-10 px-3 py-1.5 bg-gray-200 dark:bg-gray-600 border-y border-gray-300 dark:border-gray-500">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-300">Outside View ({vehiclesOutsideView.length})</span>
                        </div>
                        <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {vehiclesOutsideView.map((vehicle) => (
                          <button
                            key={vehicle.uniqueKey}
                            onClick={() => selectAndPanToVehicle(vehicle.id)}
                            className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                              selectedVehicleId === vehicle.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                            }`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <div
                                    className={`w-2 h-2 rounded-full flex-shrink-0 ${
                                      vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                                        ? 'bg-red-500'
                                        : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                                        ? ''
                                        : vehicle.dispatchInfo?.proNumber && !vehicle.dispatchInfo?.isCompleted
                                        ? 'bg-blue-500'
                                        : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                                        ? 'bg-orange-500'
                                        : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                                        ? 'bg-gray-500'
                                        : 'bg-green-500'
                                    }`}
                                    style={vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING' && vehicle.dispatchInfo?.v1Status?.toUpperCase() !== 'OUT OF SERVICE' ? { backgroundColor: '#FFFAA0' } : undefined}
                                  />
                                  <h3 className="font-medium text-heading text-sm truncate">
                                    {vehicle.dispatchInfo?.unitNumber && vehicle.dispatchInfo?.driverName
                                      ? `${vehicle.dispatchInfo.unitNumber} - ${vehicle.dispatchInfo.driverName}`
                                      : vehicle.dispatchInfo?.driverName || vehicle.dispatchInfo?.unitNumber || vehicle.name}<MuleBadge isMule={vehicle.dispatchInfo?.isMule} />
                                  </h3>
                                </div>
                                {vehicle.dispatchInfo?.proNumber ? (
                                  <div className="mt-1.5 px-2 py-1.5 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                                    <p className="text-xs text-blue-700 dark:text-blue-300">
                                      Pro: <span className="font-semibold">{vehicle.dispatchInfo.proNumber}</span>
                                    </p>
                                    {vehicle.dispatchInfo?.originName && (
                                      <div className="mt-1">
                                        <p className="text-xs text-blue-800 dark:text-blue-200 truncate flex items-center gap-1">
                                          {vehicle.dispatchInfo.originName}
                                          {!vehicle.dispatchInfo.originCoordinates && (
                                            <ExclamationCircleIcon className="w-3 h-3 text-amber-600 flex-shrink-0" title="Invalid Location" />
                                          )}
                                        </p>
                                        {(vehicle.dispatchInfo?.originArrivalDateTime || vehicle.dispatchInfo?.originDepartDateTime) && (
                                          <p className="text-xs text-blue-600 dark:text-blue-400">
                                            {vehicle.dispatchInfo?.originArrivalDateTime && (
                                              <span>Arv: {new Date(vehicle.dispatchInfo.originArrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                            )}
                                            {vehicle.dispatchInfo?.originArrivalDateTime && vehicle.dispatchInfo?.originDepartDateTime && <span> | </span>}
                                            {vehicle.dispatchInfo?.originDepartDateTime && (
                                              <span>Dep: {new Date(vehicle.dispatchInfo.originDepartDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                            )}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                    {vehicle.dispatchInfo?.destinationName && (
                                      <div className="mt-0.5">
                                        <p className="text-xs text-blue-800 dark:text-blue-200 truncate flex items-center gap-1">
                                          {vehicle.dispatchInfo.destinationName}
                                          {!vehicle.dispatchInfo.destinationCoordinates && (
                                            <ExclamationCircleIcon className="w-3 h-3 text-amber-600 flex-shrink-0" title="Invalid Location" />
                                          )}
                                        </p>
                                        {(vehicle.dispatchInfo?.arrivalDateTime || vehicle.dispatchInfo?.departDateTime) && (
                                          <p className="text-xs text-blue-600 dark:text-blue-400">
                                            {vehicle.dispatchInfo?.arrivalDateTime && (
                                              <span>Arv: {new Date(vehicle.dispatchInfo.arrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                            )}
                                            {vehicle.dispatchInfo?.arrivalDateTime && vehicle.dispatchInfo?.departDateTime && <span> | </span>}
                                            {vehicle.dispatchInfo?.departDateTime && (
                                              <span>Dep: {new Date(vehicle.dispatchInfo.departDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                            )}
                                          </p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                    {vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                                      ? 'Out Of Service'
                                      : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                                      ? 'Not Working'
                                      : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                                      ? 'Not Started'
                                      : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                                      ? 'Dispatched'
                                      : 'No Dispatch'}
                                  </p>
                                )}
                                {/* Endorsement Icons */}
                                <div className="flex gap-1.5 mt-2 items-center px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                                  {vehicle.dispatchInfo?.has93k && (
                                    <Tooltip content="93K Permit">
                                      <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}>&#xE9E5;</span>
                                    </Tooltip>
                                  )}
                                  {vehicle.dispatchInfo?.hasHazmat && (
                                    <Tooltip content="Hazmat">
                                      <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '14px' }}></span>
                                    </Tooltip>
                                  )}
                                  {vehicle.dispatchInfo?.hasTank && (
                                    <Tooltip content="Tank">
                                      <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}></span>
                                    </Tooltip>
                                  )}
                                  {vehicle.dispatchInfo?.isLightTruck && (
                                    <Tooltip content="<15K lbs">
                                      <svg viewBox="0 0 32 20" width="18" height="11" fill="currentColor" className="text-green-600 dark:text-green-400">
                                        <rect x="1" y="1" width="30" height="18" rx="3" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                                        <text x="16" y="14" textAnchor="middle" fontFamily="var(--app-font-family)" fontSize="11" fontWeight="bold">&lt;15K</text>
                                      </svg>
                                    </Tooltip>
                                  )}
                                  {vehicle.dispatchInfo?.isDaycab ? (
                                    <Tooltip content="Daycab">
                                      <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '14px' }}></span>
                                    </Tooltip>
                                  ) : (
                                    <Tooltip content="Sleeper">
                                      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-indigo-600 dark:text-indigo-400">
                                        <path d="M2,14H24v8H22V19H2v3H0V2H2Zm3.5-2A2.5,2.5,0,1,0,3,9.5,2.5,2.5,0,0,0,5.5,12ZM21,6H12A3,3,0,0,0,9,9v3H24V9A3,3,0,0,0,21,6Z"/>
                                      </svg>
                                    </Tooltip>
                                  )}
                                </div>
                                {/* Completed / Remaining counts */}
                                <div className="flex mt-2 px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                                  {vehicle.dispatchInfo?.unitNumber ? (
                                    <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="done" />} placement="left">
                                      <div className="w-1/2 text-xs text-muted cursor-help">
                                        <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div className="w-1/2 text-xs text-muted">
                                      <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                                    </div>
                                  )}
                                  {vehicle.dispatchInfo?.unitNumber ? (
                                    <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="remaining" />} placement="left">
                                      <div className="w-1/2 text-xs text-muted cursor-help">
                                        <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                                      </div>
                                    </Tooltip>
                                  ) : (
                                    <div className="w-1/2 text-xs text-muted">
                                      <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                                    </div>
                                  )}
                                </div>
                              </div>
                              <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            </div>
                          </button>
                        ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </PermissionsContainer>

      {/* Mobile bottom sheet - Vehicle list */}
      <div className="lg:hidden">
        {/* Backdrop */}
        {showMobileVehicles && (
          <div
            className="fixed inset-0 bg-scrim bg-opacity-50 z-40"
            onClick={() => setShowMobileVehicles(false)}
          />
        )}

        {/* Bottom sheet */}
        <div className={`fixed bottom-0 left-0 right-0 z-50 transform transition-transform duration-300 ease-in-out ${
          showMobileVehicles ? 'translate-y-0' : 'translate-y-[calc(100%-60px)]'
        }`}>
          <div className="bg-panel rounded-t-2xl shadow-xl max-h-[75vh] flex flex-col">
            {/* Handle bar and header */}
            <div className="flex flex-col">
              <div className="w-full p-4 flex flex-col items-center">
                <button
                  onClick={() => setShowMobileVehicles(!showMobileVehicles)}
                  className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mb-3 cursor-pointer"
                  aria-label="Toggle vehicle list"
                ></button>
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <TruckIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    <span className="text-base font-semibold text-heading">
                      {vehicleMarkers.length} Vehicles
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" className="w-2.5 h-2.5 mr-1">
                          <path d="M0,111.5 L0,0 L111.5,0 C114.538,0 117,2.462 117,5.5 L117,17 L165,17 C166.326,17 167.598,17.527 168.536,18.464 L228.536,78.464 C229.473,79.402 230,80.674 230,82 L230,155 C230,163.284 223.284,170 215,170 L209.243,170 C207.177,185.582 193.638,198 177,198 C160.362,198 146.823,185.582 144.757,170 L95.243,170 C93.177,185.582 79.638,198 63,198 C46.362,198 32.823,185.582 30.757,170 L25,170 C16.716,170 10,163.284 10,155 L10,111.5 L0,111.5 Z M177,172 C188.046,172 197,163.046 197,152 C197,140.954 188.046,132 177,132 C165.954,132 157,140.954 157,152 C157,163.046 165.954,172 177,172 Z M63,172 C74.046,172 83,163.046 83,152 C83,140.954 74.046,132 63,132 C51.954,132 43,140.954 43,152 C43,163.046 51.954,172 63,172 Z M24,33 L24,78 L66,78 L66,33 L24,33 Z"
                            fill="#3B82F6"
                            stroke="#ffffff"
                            strokeWidth="8"
                          />
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stats.inProgress}</span>
                      </span>
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 239 205" className="w-2.5 h-2.5 mr-1">
                          <path d="M230.4,102.399L128,102.399C123.29,102.399 119.467,98.576 119.467,93.866L119.467,8.533C119.467,3.823 115.644,0 110.934,0L42.667,0C39.177,0 36.037,2.125 34.74,5.367L0.614,90.683C0.41,91.758 0.205,92.799 0,93.866L0,179.199C0,183.909 3.823,187.732 8.533,187.732L38.878,187.732C44.8,197.887 55.689,204.799 68.267,204.799C87.092,204.799 102.4,189.49 102.4,170.666L136.533,170.666C136.533,189.491 151.842,204.799 170.666,204.799C189.49,204.799 204.8,189.491 204.8,170.666L230.4,170.666C235.11,170.666 238.933,166.843 238.933,162.133L238.933,110.933C238.933,106.222 235.11,102.399 230.4,102.399ZM27.563,85.333C24.508,85.333 22.443,82.227 23.629,79.411L41.575,36.744C42.232,35.165 43.785,34.133 45.5,34.133L72.534,34.133C74.889,34.133 76.801,36.044 76.801,38.4L76.801,81.066C76.801,83.421 74.89,85.333 72.534,85.333L27.563,85.333ZM68.267,187.733C58.855,187.733 51.2,180.079 51.2,170.666C51.2,161.254 58.854,153.599 68.267,153.599C77.679,153.599 85.334,161.253 85.334,170.666C85.333,180.078 77.679,187.733 68.267,187.733ZM170.667,187.733C161.255,187.733 153.6,180.079 153.6,170.666C153.6,161.254 161.254,153.599 170.667,153.599C180.079,153.599 187.734,161.253 187.734,170.666C187.733,180.078 180.079,187.733 170.667,187.733Z"
                            fill="#6B7280"
                          />
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stats.dispatched}</span>
                      </span>
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" className="w-2.5 h-2.5 mr-1">
                          <path d="M0,111.5 L0,0 L111.5,0 C114.538,0 117,2.462 117,5.5 L117,17 L165,17 C166.326,17 167.598,17.527 168.536,18.464 L228.536,78.464 C229.473,79.402 230,80.674 230,82 L230,155 C230,163.284 223.284,170 215,170 L209.243,170 C207.177,185.582 193.638,198 177,198 C160.362,198 146.823,185.582 144.757,170 L95.243,170 C93.177,185.582 79.638,198 63,198 C46.362,198 32.823,185.582 30.757,170 L25,170 C16.716,170 10,163.284 10,155 L10,111.5 L0,111.5 Z M177,172 C188.046,172 197,163.046 197,152 C197,140.954 188.046,132 177,132 C165.954,132 157,140.954 157,152 C157,163.046 165.954,172 177,172 Z M63,172 C74.046,172 83,163.046 83,152 C83,140.954 74.046,132 63,132 C51.954,132 43,140.954 43,152 C43,163.046 51.954,172 63,172 Z M24,33 L24,78 L66,78 L66,33 L24,33 Z"
                            fill="#10B981"
                            stroke="#ffffff"
                            strokeWidth="8"
                          />
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stats.available}</span>
                      </span>
                      <span className="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" className="w-2.5 h-2.5 mr-1">
                          <path d="M0,111.5 L0,0 L111.5,0 C114.538,0 117,2.462 117,5.5 L117,17 L165,17 C166.326,17 167.598,17.527 168.536,18.464 L228.536,78.464 C229.473,79.402 230,80.674 230,82 L230,155 C230,163.284 223.284,170 215,170 L209.243,170 C207.177,185.582 193.638,198 177,198 C160.362,198 146.823,185.582 144.757,170 L95.243,170 C93.177,185.582 79.638,198 63,198 C46.362,198 32.823,185.582 30.757,170 L25,170 C16.716,170 10,163.284 10,155 L10,111.5 L0,111.5 Z M177,172 C188.046,172 197,163.046 197,152 C197,140.954 188.046,132 177,132 C165.954,132 157,140.954 157,152 C157,163.046 165.954,172 177,172 Z M63,172 C74.046,172 83,163.046 83,152 C83,140.954 74.046,132 63,132 C51.954,132 43,140.954 43,152 C43,163.046 51.954,172 63,172 Z M24,33 L24,78 L66,78 L66,33 L24,33 Z"
                            fill="#EF4444"
                            stroke="#ffffff"
                            strokeWidth="8"
                          />
                        </svg>
                        <span className="text-xs text-gray-600 dark:text-gray-400">{stats.outOfService}</span>
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Tooltip content={isListLocked ? "Unlock Driver List" : "Lock Driver List"}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsListLocked(!isListLocked);
                        }}
                        className={`p-1.5 rounded-lg transition-colors ${
                          isListLocked
                            ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20'
                            : 'text-muted hover:text-gray-700 dark:hover:text-gray-200'
                        }`}
                        aria-label={isListLocked ? "Unlock vehicle list" : "Lock vehicle list"}
                      >
                        {isListLocked ? (
                          <LockClosedIcon className="w-5 h-5" />
                        ) : (
                          <LockOpenIcon className="w-5 h-5" />
                        )}
                      </button>
                    </Tooltip>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowSearch(!showSearch);
                        if (showSearch) setSearchQuery('');
                      }}
                      className="p-1.5 text-muted hover:text-gray-700 dark:hover:text-gray-200"
                      aria-label="Search vehicles"
                    >
                      {showSearch ? (
                        <XMarkIcon className="w-5 h-5" />
                      ) : (
                        <MagnifyingGlassIcon className="w-5 h-5" />
                      )}
                    </button>
                    <button
                      onClick={() => setShowMobileVehicles(!showMobileVehicles)}
                      className="p-1"
                      aria-label="Toggle vehicle list"
                    >
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${
                          showMobileVehicles ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* Search input for mobile */}
              {showSearch && showMobileVehicles && (
                <div className="px-4 pb-3 border-b border-gray-200 dark:border-gray-700">
                  <PermissionAwareInput
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search by unit number or name..."
                    className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:text-white dark:placeholder-gray-400"
                    autoFocus
                  />
                </div>
              )}
            </div>

            {/* Scrollable vehicle list */}
            <div className={`overflow-y-auto flex-1 divide-y divide-gray-200 dark:divide-gray-700 ${
              showMobileVehicles ? 'block' : 'hidden'
            }`}>
              {vehicleMarkers.length === 0 ? (
                <div className="p-4 text-center text-muted">
                  No vehicles to display
                </div>
              ) : (
                vehicleMarkers.map((vehicle) => (
                  <button
                    key={vehicle.uniqueKey}
                    onClick={() => {
                      selectAndPanToVehicle(vehicle.id);
                      setShowMobileVehicles(false);
                    }}
                    className={`w-full p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-700 active:bg-gray-100 dark:active:bg-gray-600 transition-colors ${
                      selectedVehicleId === vehicle.id ? 'bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-2 h-2 rounded-full flex-shrink-0 ${
                              vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                                ? 'bg-red-500'
                                : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                                ? ''
                                : vehicle.dispatchInfo?.proNumber && !vehicle.dispatchInfo?.isCompleted
                                ? 'bg-blue-500'
                                : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                                ? 'bg-orange-500'
                                : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                                ? 'bg-gray-500'
                                : 'bg-green-500'
                            }`}
                            style={vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING' && vehicle.dispatchInfo?.v1Status?.toUpperCase() !== 'OUT OF SERVICE' ? { backgroundColor: '#FFFAA0' } : undefined}
                          />
                          <h3 className="font-medium text-heading text-sm">
                            {vehicle.dispatchInfo?.unitNumber && vehicle.dispatchInfo?.driverName
                              ? `${vehicle.dispatchInfo.unitNumber} - ${vehicle.dispatchInfo.driverName}`
                              : vehicle.dispatchInfo?.driverName || vehicle.dispatchInfo?.unitNumber || vehicle.name}<MuleBadge isMule={vehicle.dispatchInfo?.isMule} />
                          </h3>
                        </div>
                        {vehicle.dispatchInfo?.proNumber ? (
                          <div className="mt-1.5 px-2 py-1.5 bg-blue-50/50 dark:bg-blue-900/20 rounded-md border border-blue-200 dark:border-blue-700">
                            <p className="text-xs text-blue-700 dark:text-blue-300">
                              Pro: <span className="font-semibold">{vehicle.dispatchInfo.proNumber}</span>
                            </p>
                            {vehicle.dispatchInfo?.originName && (
                              <div className="mt-1">
                                <p className="text-xs text-blue-800 dark:text-blue-200 truncate">
                                  {vehicle.dispatchInfo.originName}
                                </p>
                                {(vehicle.dispatchInfo?.originArrivalDateTime || vehicle.dispatchInfo?.originDepartDateTime) && (
                                  <p className="text-xs text-blue-600 dark:text-blue-400">
                                    {vehicle.dispatchInfo?.originArrivalDateTime && (
                                      <span>Arv: {new Date(vehicle.dispatchInfo.originArrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                    )}
                                    {vehicle.dispatchInfo?.originArrivalDateTime && vehicle.dispatchInfo?.originDepartDateTime && <span> | </span>}
                                    {vehicle.dispatchInfo?.originDepartDateTime && (
                                      <span>Dep: {new Date(vehicle.dispatchInfo.originDepartDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            )}
                            {vehicle.dispatchInfo?.destinationName && (
                              <div className="mt-0.5">
                                <p className="text-xs text-blue-800 dark:text-blue-200 truncate">
                                  {vehicle.dispatchInfo.destinationName}
                                </p>
                                {(vehicle.dispatchInfo?.arrivalDateTime || vehicle.dispatchInfo?.departDateTime) && (
                                  <p className="text-xs text-blue-600 dark:text-blue-400">
                                    {vehicle.dispatchInfo?.arrivalDateTime && (
                                      <span>Arv: {new Date(vehicle.dispatchInfo.arrivalDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                    )}
                                    {vehicle.dispatchInfo?.arrivalDateTime && vehicle.dispatchInfo?.departDateTime && <span> | </span>}
                                    {vehicle.dispatchInfo?.departDateTime && (
                                      <span>Dep: {new Date(vehicle.dispatchInfo.departDateTime).toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).replace(',', '')}</span>
                                    )}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            {vehicle.dispatchInfo?.v1Status?.toUpperCase() === 'OUT OF SERVICE'
                              ? 'Out Of Service'
                              : vehicle.dispatchInfo?.driverStatus?.toUpperCase() === 'NOT WORKING'
                              ? 'Not Working'
                              : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0 && !vehicle.dispatchInfo?.originArrivalDateTime
                              ? 'Not Started'
                              : (vehicle.dispatchInfo?.remainingDispatchesToday || 0) > 0
                              ? 'Dispatched'
                              : 'No Dispatch'}
                          </p>
                        )}
                        {/* Endorsement Icons */}
                        <div className="flex gap-1.5 mt-2 items-center px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                          {vehicle.dispatchInfo?.has93k && (
                            <Tooltip content="93K Permit">
                              <span className="gw-icon text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}>&#xE9E5;</span>
                            </Tooltip>
                          )}
                          {vehicle.dispatchInfo?.hasHazmat && (
                            <Tooltip content="Hazmat">
                              <span className="gw-icon gw-icon-hazmat text-orange-500" style={{ fontSize: '14px' }}></span>
                            </Tooltip>
                          )}
                          {vehicle.dispatchInfo?.hasTank && (
                            <Tooltip content="Tank">
                              <span className="gw-icon gw-icon-tank text-purple-600 dark:text-purple-400" style={{ fontSize: '14px' }}></span>
                            </Tooltip>
                          )}
                          {vehicle.dispatchInfo?.isLightTruck && (
                            <Tooltip content="<15K lbs">
                              <svg viewBox="0 0 32 20" width="18" height="11" fill="currentColor" className="text-green-600 dark:text-green-400">
                                <rect x="1" y="1" width="30" height="18" rx="3" opacity="0.15" stroke="currentColor" strokeWidth="1.5"/>
                                <text x="16" y="14" textAnchor="middle" fontFamily="var(--app-font-family)" fontSize="11" fontWeight="bold">&lt;15K</text>
                              </svg>
                            </Tooltip>
                          )}
                          {vehicle.dispatchInfo?.isDaycab ? (
                            <Tooltip content="Daycab">
                              <span className="gw-icon gw-icon-daycab text-blue-600 dark:text-blue-400" style={{ fontSize: '14px' }}></span>
                            </Tooltip>
                          ) : (
                            <Tooltip content="Sleeper">
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" className="text-indigo-600 dark:text-indigo-400">
                                <path d="M2,14H24v8H22V19H2v3H0V2H2Zm3.5-2A2.5,2.5,0,1,0,3,9.5,2.5,2.5,0,0,0,5.5,12ZM21,6H12A3,3,0,0,0,9,9v3H24V9A3,3,0,0,0,21,6Z"/>
                              </svg>
                            </Tooltip>
                          )}
                        </div>
                        {/* Completed / Remaining counts */}
                        <div className="flex mt-2 px-2 py-1.5 bg-gray-50/50 dark:bg-gray-800/50 rounded-md border border-rule">
                          {vehicle.dispatchInfo?.unitNumber ? (
                            <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="done" />} placement="left">
                              <div className="w-1/2 text-xs text-muted cursor-help">
                                <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                              </div>
                            </Tooltip>
                          ) : (
                            <div className="w-1/2 text-xs text-muted">
                              <span className="text-green-600 dark:text-green-400 font-medium">{vehicle.dispatchInfo?.isCompleted ? 1 : 0}</span> done
                            </div>
                          )}
                          {vehicle.dispatchInfo?.unitNumber ? (
                            <Tooltip content={<DispatchesTooltipContent unitNumber={vehicle.dispatchInfo.unitNumber} type="remaining" />} placement="left">
                              <div className="w-1/2 text-xs text-muted cursor-help">
                                <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                              </div>
                            </Tooltip>
                          ) : (
                            <div className="w-1/2 text-xs text-muted">
                              <span className="text-orange-600 dark:text-orange-400 font-medium">{vehicle.dispatchInfo?.remainingDispatchesToday || 0}</span> remaining
                            </div>
                          )}
                        </div>
                      </div>
                      <ChevronRightIcon className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

    </div>

    {/* Pro Details Modal */}
    {selectedProNumber && (
      <ProDetailsView
        pronumber={selectedProNumber}
        isOpen={isProDetailsModalOpen}
        onClose={() => {
          setIsProDetailsModalOpen(false);
          setSelectedProNumber(null);
        }}
        ProNumberLink={({ pronumber }) => (
          <a
            href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 dark:text-blue-400 hover:underline"
          >
            {pronumber}
          </a>
        )}
      />
    )}
    </>
  );
};

export default LiveMap;